import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';
import {
  DEFAULT_ASSET_FILTERS,
  applyAssetFilters,
  filtersFromAssetView,
  isFiltersDefault,
  type AssetFilters,
  type BusinessAssetView,
} from '../../utils/assetFilters';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Autocomplete,
  CardMedia,
  CardActions,
  Chip,
  ButtonGroup,
  Snackbar,
  Checkbox,
  Tooltip,
  Divider,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, CloudUpload as CloudUploadIcon, Image as ImageIcon, LocationOn as LocationOnIcon, Fingerprint as FingerprintIcon, DirectionsCar as DirectionsCarIcon, CheckCircle as CheckCircleIcon, QrCode2 as QrCodeIcon, Assessment as AssessmentIcon, Warning as WarningIcon, History as HistoryIcon, Refresh as RefreshIcon, Person as PersonIcon, AutoAwesome as AutoAwesomeIcon, DragIndicator as DragIndicatorIcon, Reorder as ReorderIcon, Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import CheckResponses from './CheckResponses';
import QRCode from 'react-qr-code';
import safetyLogo from '../../assets/safety.png';
import PageHeader from '../../components/PageHeader';
import AssetFilesExplorer from '../../components/AssetFilesExplorer';
import {
  sectionLabelSx,
  sectionTitleSx,
  pagePaperSx,
  pageCardSx,
  pageTabsSx,
  primaryButtonSx,
  assetGridSx,
} from '../../styles/adminPageStyles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enGB from 'date-fns/locale/en-GB';

interface AssetType {
  asset_type_id: string;
  type_name: string;
  description: string;
  business_id: string;
  requires_car_registration?: boolean;
  is_global?: number;
  has_whole_good_number?: boolean;
  has_recorded_hours?: boolean;
  mark_out_of_action_on_fail?: boolean | number;
  allow_loan_attachments?: boolean | number;
}

interface JobTag {
  job_tag_id: string;
  tag_name: string;
  description?: string;
  is_active?: boolean;
  /** 'both' (default) | 'user' | 'asset' — controls where this tag may be assigned */
  tag_scope?: 'both' | 'user' | 'asset';
}

/** Persists tag filter per business so multi-business managers don’t carry the wrong tag UUID across workspaces */
function getAssetsJobTagFilterKey(): string {
  const bid = localStorage.getItem('businessId');
  return bid ? `adminAssetsJobTag_${bid}` : 'adminAssetsJobTag';
}

interface CustomField {
  custom_field_id: string;
  field_name: string;
  field_label: string;
  field_type: 'text' | 'number' | 'date' | 'textarea' | 'select' | 'checkbox';
  field_options?: string[];
  is_required: boolean;
  default_value?: string;
  field_order: number;
  placeholder?: string;
  help_text?: string;
}

interface Asset {
  asset_id: string;
  asset_name: string;
  asset_type_id: string;
  asset_type_name: string;
  serial_number: string;
  qr_code?: string;
  third_party_qr?: string;
  location_id: string;
  location_name: string;
  status: string;
  manufacturer?: string;
  model?: string;
  purchase_date?: string;
  photo_url?: string;
  last_check_date?: string;
  number_plate?: string;
  description?: string;
  tags?: string | any;
  job_tags?: JobTag[];
  is_loaned_out?: boolean;
  is_overdue?: boolean;
  recorded_hours?: number | null;
  has_recorded_hours?: boolean;
  is_out_of_action?: boolean;
  out_of_action_note?: string;
  out_of_action_date?: string;
  locked_by_user_id?: string | null;
  locked_by_name?: string | null;
  locked_at?: string | null;
  locked_check_id?: string | null;
  enabled_for_offline?: boolean;
  mot_status?: string | null;
  mot_due_date?: string | null;
  mot_last_checked_at?: string | null;
  tax_status?: string | null;
  tax_due_date?: string | null;
  tax_last_checked_at?: string | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  vehicle_colour?: string | null;
  vehicle_fuel_type?: string | null;
  vehicle_year?: number | null;
  primary_user_id?: string | null;
  primary_user_name?: string | null;
  primary_member_id?: string | null;
  primary_member_name?: string | null;
  sort_order?: number | null;
}

interface Location {
  locationId: string;
  locationName: string;
  address: string;
  contact_person: string;
  contact_phone: string;
}

interface FormData {
  asset_id?: string;
  asset_name: string;
  asset_type_id: string;
  serial_number: string;
  qr_code?: string;
  third_party_qr?: string;
  location_id: string;
  manufacturer: string;
  model: string;
  purchase_date: string;
  photo_url: string;
  number_plate: string;
  description?: string;
  recorded_hours?: string;
  is_out_of_action?: boolean;
  out_of_action_note?: string;
  enabled_for_offline?: boolean;
  job_tag_ids?: string[];
  primary_user_id?: string | null;
  primary_member_id?: string | null;
  primary_member_name?: string | null;
}

interface AssetAttachment {
  attachment_id: string;
  file_url: string;
  file_name: string;
  file_type: 'image' | 'pdf' | 'document';
  mime_type?: string;
  file_size?: number;
  display_order: number;
  created_at?: string;
}

interface AuditLog {
  audit_id: string;
  asset_id: string;
  asset_name: string;
  action: 'marked_out_of_action' | 'marked_available';
  note: string | null;
  image_urls: string | null;
  latitude: number | null;
  longitude: number | null;
  safety_check_id: string | null;
  related_safety_check_id: string | null;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

function formatDateTime(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatImportDate(value?: string) {
  if (!value) return '-';
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }
  return value;
}

// ── DocRow: single document row in the Key Documents section ─────────────────
// Defined outside the main component so it doesn't re-create on every render.
const DocRow: React.FC<{
  doc: any;
  idx?: number;
  total?: number;
  onDelete: () => void;
  onReview: () => void;
  reviewLoading: boolean;
  review: any | null;
  onDismissReview: () => void;
  onEdit?: () => void;
  onRename?: () => void;
  docTypeLabel: (t: string) => string;
  docExpiryStatus: (e?: string) => 'ok' | 'soon' | 'expired' | 'none';
  expiryChipProps: (s: any, e?: string) => { label: string; color: any };
}> = ({ doc, idx = 0, total = 1, onDelete, onReview, reviewLoading, review, onDismissReview, onEdit, onRename, docTypeLabel, docExpiryStatus, expiryChipProps }) => {
  const expiryStatus = docExpiryStatus(doc.expiry_date);
  const chip = expiryChipProps(expiryStatus, doc.expiry_date);
  const isLast = idx === total - 1;

  const verdictColor = (v: string) => {
    if (v === 'COMPLIANT') return 'success';
    if (v === 'NEEDS_IMPROVEMENT') return 'warning';
    if (v === 'NON_COMPLIANT') return 'error';
    return 'default';
  };

  return (
    <Box sx={{ borderBottom: isLast ? 0 : 1, borderColor: 'divider' }}>
      {/* Main row */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
        bgcolor: expiryStatus === 'expired' ? '#fff5f5' : expiryStatus === 'soon' ? '#fffaf0' : 'transparent',
        '&:hover': { bgcolor: expiryStatus === 'expired' ? '#fff0f0' : expiryStatus === 'soon' ? '#fff7e6' : 'action.hover' },
      }}>
        <Typography sx={{ fontSize: 18, minWidth: 24, textAlign: 'center' }}>
          {doc.file_type === 'pdf' ? '📄' : doc.file_type === 'image' ? '🖼️' : doc.file_type === 'text' ? '📝' : '📎'}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25, alignItems: 'center' }}>
            <Chip size="small" label={docTypeLabel(doc.document_type)} variant="outlined"
              sx={{ fontSize: '0.65rem', height: 18, borderRadius: 1 }} />
            {doc.source === 'check_pdf' && (
              <Chip size="small" label="Auto" color="info" variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18, borderRadius: 1 }} />
            )}
            {expiryStatus !== 'none' && (
              <Chip size="small" {...chip} sx={{ fontSize: '0.65rem', height: 18, borderRadius: 1, fontWeight: expiryStatus !== 'ok' ? 700 : 400 }} />
            )}
            {review && (
              <Chip size="small" label={review.verdict?.replace('_', ' ')}
                color={verdictColor(review.verdict) as any}
                sx={{ fontSize: '0.65rem', height: 18, borderRadius: 1, fontWeight: 700 }} />
            )}
          </Box>
          {doc.created_at && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25 }}>
              {formatDateTime(doc.created_at)}
            </Typography>
          )}
        </Box>
        <Tooltip title="AI Review — read document and assess compliance">
          <span>
            <IconButton size="small" onClick={onReview} disabled={reviewLoading}
              sx={{ color: 'secondary.main', opacity: reviewLoading ? 0.5 : 0.8, '&:hover': { opacity: 1 } }}>
              {reviewLoading
                ? <CircularProgress size={14} color="inherit" />
                : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </span>
        </Tooltip>
        {onRename && (
          <Tooltip title="Rename file">
            <IconButton size="small" onClick={onRename} sx={{ color: 'primary.main', opacity: 0.75, '&:hover': { opacity: 1 } }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {(doc.file_type === 'text' || doc.mime_type === 'text/plain') && onEdit && (
          <Tooltip title="Edit text content">
            <IconButton size="small" onClick={onEdit}
              sx={{ color: 'primary.main', opacity: 0.75, '&:hover': { opacity: 1 } }}>
              <Typography sx={{ fontSize: 14 }}>✏️</Typography>
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Open">
          <IconButton size="small" component="a" href={doc.file_url} target="_blank" rel="noopener noreferrer">
            <Typography sx={{ fontSize: 13, lineHeight: 1 }}>↗</Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={onDelete} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* AI Review result panel */}
      {review && (
        <Box sx={{
          mx: 1.5, mb: 1, p: 1.5, borderRadius: 2,
          bgcolor: review.verdict === 'COMPLIANT' ? 'success.50' : review.verdict === 'NON_COMPLIANT' ? 'error.50' : review.verdict === 'NEEDS_IMPROVEMENT' ? 'warning.50' : 'grey.50',
          border: 1,
          borderColor: review.verdict === 'COMPLIANT' ? 'success.200' : review.verdict === 'NON_COMPLIANT' ? 'error.200' : review.verdict === 'NEEDS_IMPROVEMENT' ? 'warning.200' : 'divider',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip size="small" label={review.verdict?.replace(/_/g, ' ')} color={verdictColor(review.verdict) as any} sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
              <Chip size="small" label={`Confidence: ${review.confidence}`} variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{review.summary}</Typography>
            </Box>
            <IconButton size="small" onClick={onDismissReview} sx={{ p: 0.25, ml: 1 }}>
              <Typography sx={{ fontSize: 12, lineHeight: 1 }}>✕</Typography>
            </IconButton>
          </Box>

          {review.what_is_present?.length > 0 && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.dark', display: 'block', mb: 0.25 }}>✓ Present & correct</Typography>
              {review.what_is_present.map((item: string, i: number) => (
                <Typography key={i} variant="caption" display="block" sx={{ pl: 1, color: 'text.secondary' }}>• {item}</Typography>
              ))}
            </Box>
          )}

          {review.what_is_missing?.length > 0 && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.dark', display: 'block', mb: 0.25 }}>✗ Missing / gaps</Typography>
              {review.what_is_missing.map((item: string, i: number) => (
                <Typography key={i} variant="caption" display="block" sx={{ pl: 1, color: 'text.secondary' }}>• {item}</Typography>
              ))}
            </Box>
          )}

          {review.recommendations?.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark', display: 'block', mb: 0.25 }}>Recommendations</Typography>
              {review.recommendations.map((item: string, i: number) => (
                <Typography key={i} variant="caption" display="block" sx={{ pl: 1, color: 'text.secondary' }}>→ {item}</Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

type AssetsPageTab = 'assets' | 'files' | 'types' | 'import' | 'map' | 'audit';
const ASSETS_PAGE_TABS: AssetsPageTab[] = ['assets', 'files', 'types', 'import', 'map', 'audit'];

type AssetDialogTab = 'details' | 'people' | 'files' | 'documents' | 'settings';

const AssetManagement: React.FC<{ standaloneViewId?: string }> = ({ standaloneViewId }) => {
  const isStandaloneView = Boolean(standaloneViewId);
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [assets, setAssets] = useState<Asset[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [allowMotTaxTab, setAllowMotTaxTab] = useState(false);
  const [allowKeyDocuments, setAllowKeyDocuments] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [assetDialogTab, setAssetDialogTab] = useState<AssetDialogTab>('details');
  const [openAssetTypeDialog, setOpenAssetTypeDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [editingAssetType, setEditingAssetType] = useState<AssetType | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<AssetsPageTab>(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab && ASSETS_PAGE_TABS.includes(tab as AssetsPageTab)
      ? (tab as AssetsPageTab)
      : 'assets';
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    asset_name: '',
    asset_type_id: '',
    serial_number: '',
    qr_code: '',
    third_party_qr: '',
    location_id: '',
    manufacturer: '',
    model: '',
    purchase_date: '',
    photo_url: '',
    number_plate: '',
    description: '',
    recorded_hours: '',
    is_out_of_action: false,
    out_of_action_note: '',
    enabled_for_offline: false,
    primary_user_id: null,
    primary_member_id: null,
    primary_member_name: null,
  });
  const [assetTypeFormData, setAssetTypeFormData] = useState({
    type_name: '',
    description: '',
    requires_car_registration: false,
    has_whole_good_number: false,
    has_recorded_hours: false,
    mark_out_of_action_on_fail: true,
    allow_loan_attachments: false,
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentFileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<AssetAttachment[]>([]);

  // ── Key Documents & RAMS state ──────────────────────────────────────────────
  interface AssetDocument {
    document_id: string;
    asset_id: string;
    folder_id: string | null;
    folder_name?: string;
    document_type: string;
    title: string;
    file_url: string;
    file_name: string;
    file_type: string;
    mime_type?: string;
    file_size?: number;
    issue_date?: string;
    expiry_date?: string;
    is_current: number;
    source: 'manual' | 'check_pdf';
    check_type?: string;
    notes?: string;
    first_name?: string;
    last_name?: string;
    created_at: string;
  }
  interface DocFolder { folder_id: string; asset_id: string; folder_name: string; }
  interface AiAnalysis {
    overall_status: 'COMPLIANT' | 'NEEDS_ATTENTION' | 'NON_COMPLIANT';
    summary: string;
    required_documents: { document_type: string; label: string; status: string; notes?: string }[];
    action_items: string[];
    analysis_text: string;
  }

  const [assetDocuments, setAssetDocuments] = useState<AssetDocument[]>([]);
  const [docFolders, setDocFolders] = useState<DocFolder[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<AiAnalysis | null>(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [docReviews, setDocReviews] = useState<Record<string, any>>({});
  const [docReviewLoading, setDocReviewLoading] = useState<Record<string, boolean>>({});
  const [addDocDialogOpen, setAddDocDialogOpen] = useState(false);
  const [textEditorOpen, setTextEditorOpen] = useState(false);
  const [textEditorDoc, setTextEditorDoc] = useState<any | null>(null);
  const [textEditorContent, setTextEditorContent] = useState('');
  const [textEditorLoading, setTextEditorLoading] = useState(false);
  const [textEditorSaving, setTextEditorSaving] = useState(false);
  const [newTextDocDialogOpen, setNewTextDocDialogOpen] = useState(false);
  const [newTextDocForm, setNewTextDocForm] = useState({
    document_type: 'other', title: '', folder_id: '', issue_date: '', expiry_date: '', notes: '', content: '',
  });
  const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{ type: 'folder' | 'file'; id: string; name: string } | null>(null);
  const [renameName, setRenameName] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [newDocForm, setNewDocForm] = useState({
    document_type: 'other',
    title: '',
    folder_id: '',
    issue_date: '',
    expiry_date: '',
    notes: '',
    file: null as File | null,
  });
  const docFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [settingMainAttachmentId, setSettingMainAttachmentId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrData, setQrData] = useState<{ assetName: string; url: string; identifierLabel: string; identifierValue: string } | null>(null);
  
  // Note view dialog state
  const [noteDialog, setNoteDialog] = useState<{
    open: boolean;
    title: string;
    note: string;
    assetName?: string;
  }>({
    open: false,
    title: '',
    note: '',
    assetName: '',
  });
  
  // Out of action dialog state
  const [outOfActionDialog, setOutOfActionDialog] = useState<{
    open: boolean;
    asset: Asset | null;
    note: string;
    isOutOfAction: boolean;
    images: File[];
    uploadingImages: boolean;
  }>({
    open: false,
    asset: null,
    note: '',
    isOutOfAction: false,
    images: [],
    uploadingImages: false
  });

  // Tracks asset IDs currently having an MOT check run in the background
  const [motCheckingIds, setMotCheckingIds] = useState<Set<string>>(new Set());

  // MOT history modal state
  const [motHistoryModal, setMotHistoryModal] = useState<{
    open: boolean;
    asset: Asset | null;
    history: any[];
    loading: boolean;
    refreshing: boolean;
  }>({
    open: false,
    asset: null,
    history: [],
    loading: false,
    refreshing: false,
  });

  // Import state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importValidating, setImportValidating] = useState(false);
  const [importExecuting, setImportExecuting] = useState(false);
  const [validatedData, setValidatedData] = useState<any>(null);
  const [duplicateDecisions, setDuplicateDecisions] = useState<Record<number, 'skip' | 'update' | 'create'>>({});
  const [importResults, setImportResults] = useState<any>(null);

  // Custom fields state
  const [customFieldsDialog, setCustomFieldsDialog] = useState(false);
  const [selectedAssetTypeForCustomFields, setSelectedAssetTypeForCustomFields] = useState<AssetType | null>(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loadingCustomFields, setLoadingCustomFields] = useState(false);
  const [customFieldFormDialog, setCustomFieldFormDialog] = useState(false);
  const [editingCustomField, setEditingCustomField] = useState<CustomField | null>(null);
  const [customFieldFormData, setCustomFieldFormData] = useState({
    field_name: '',
    field_label: '',
    field_type: 'text' as 'text' | 'number' | 'date' | 'textarea' | 'select' | 'checkbox',
    field_options: [] as string[],
    is_required: false,
    default_value: '',
    field_order: 0,
    placeholder: '',
    help_text: ''
  });
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});

  // Map state
  const [mapData, setMapData] = useState<Array<{
    asset_id: string;
    asset_name: string;
    asset_type_name?: string;
    photo_url?: string;
    number_plate?: string;
    latitude: number | null;
    longitude: number | null;
    location_updated_at?: string | null;
    location_status?: string | null;
    last_check?: {
      safety_check_id: string;
      completed_at: string;
      check_name: string;
      check_type: string;
    } | null;
    loan_info?: {
      borrower_name?: string | null;
      address?: string | null;
      full_address?: any;
    } | null;
  }>>([]);
  const [loadingMap, setLoadingMap] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [selectedMapAsset, setSelectedMapAsset] = useState<string | null>(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const mapRef = useRef<any>(null);
  // MOT/Tax tab filter state
  // Job tags state
  const [jobTags, setJobTags] = useState<JobTag[]>([]);
  /** Logged-in user's job tag ids (from GET /auth/me) — for default "my role" filter */
  const [userJobTagIds, setUserJobTagIds] = useState<string[]>([]);
  /** Logged-in user's ID (from GET /auth/me) — for "my primary assets" grouping */
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Asset search and filter state
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [assetFilters, setAssetFilters] = useState<AssetFilters>(DEFAULT_ASSET_FILTERS);
  const [standaloneView, setStandaloneView] = useState<BusinessAssetView | null>(null);
  const [standaloneViewLoading, setStandaloneViewLoading] = useState(isStandaloneView);
  const [standaloneViewError, setStandaloneViewError] = useState<string | null>(null);

  const [unlockDialog, setUnlockDialog] = useState<{
    open: boolean;
    asset: Asset | null;
    checkResponses: any[];
    loadingResponses: boolean;
    unlocking: boolean;
  }>({ open: false, asset: null, checkResponses: [], loadingResponses: false, unlocking: false });

  const [selectedAssetForDialog, setSelectedAssetForDialog] = useState<null | {
    asset_id: string;
    asset_name: string;
    last_check_id?: string;
  }>(null);
  const [selectedAssetForChecks, setSelectedAssetForChecks] = useState<null | {
    asset_id: string;
    asset_name: string;
  }>(null);

  const [businessUsers, setBusinessUsers] = useState<{ userId: string; firstName?: string; lastName?: string; email: string; isSharedAccountMember?: boolean; sharedAccountName?: string }[]>([]);

  // Location history state
  const [locationHistoryDialog, setLocationHistoryDialog] = useState<null | {
    asset_id: string;
    asset_name: string;
  }>(null);
  const [locationHistory, setLocationHistory] = useState<Array<{
    id: string;
    asset_id: string;
    latitude: number;
    longitude: number;
    is_home_location: boolean;
    location_name: string;
    created_at: string;
    source: string;
    check_type?: string;
    check_name?: string;
    completed_at?: string;
    source_description: string;
    loan_address?: string | null;
    borrower_name?: string | null;
  }>>([]);
  const [loadingLocationHistory, setLoadingLocationHistory] = useState(false);
  const [showLocationHistory, setShowLocationHistory] = useState(false);
  const [historicalLocations, setHistoricalLocations] = useState<Array<{
    asset_id: string;
    asset_name: string;
    locations: Array<{
      id: string;
      latitude: number;
      longitude: number;
      source: string;
      created_at: string;
      source_description: string;
      loan_address?: string | null;
      borrower_name?: string | null;
    }>;
  }>>([]);

  // Fix Leaflet default icon
  delete (L.Icon.Default.prototype as any)._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });

  // Default placeholder image as base64 data URL for missing asset images
  const placeholderImage = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjAwIDIwMCI+CiAgPHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNlZWVlZWUiLz4KICA8dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIyMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgYWxpZ25tZW50LWJhc2VsaW5lPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZmlsbD0iIzk5OTk5OSI+Tm8gSW1hZ2UgQXZhaWxhYmxlPC90ZXh0Pgo8L3N2Zz4=";

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle both array response and success/data response format
      if (Array.isArray(response.data)) {
        setLocations(response.data);
      } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setLocations(response.data.data);
      } else {
        console.error('Unexpected response format:', response.data);
        setLocations([]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setError('Failed to fetch locations');
    }
  };

  const fetchAssetTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/asset-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setAssetTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching asset types:', error);
      setError('Failed to fetch asset types');
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(assets);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setAssets(reordered);
    saveReorder(reordered.map(a => a.asset_id));
  };

  const saveReorder = async (orderedIds: string[]) => {
    try {
      setIsSavingOrder(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/assets/reorder`, { asset_ids: orderedIds }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Error saving asset order:', err);
    } finally {
      setIsSavingOrder(false);
    }
  };

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`${API_BASE_URL}/assets`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      logger.debug('Fetched assets:', response.data);

      // Helper function to parse tags and determine loan status
      const parseLoanStatus = (tags: any): { is_loaned_out: boolean; is_overdue: boolean } => {
        let is_loaned_out = false;
        let is_overdue = false;

        if (!tags) {
          return { is_loaned_out: false, is_overdue: false };
        }

        try {
          // Handle JSON string or object
          let parsedTags: any;
          if (typeof tags === 'string') {
            parsedTags = JSON.parse(tags);
          } else {
            parsedTags = tags;
          }

          if (parsedTags) {
            if (Array.isArray(parsedTags)) {
              // Array of tag objects — if ANY entry has status 'returned', treat as NOT loaned out
              // (a 'returned' entry always takes precedence over a stale 'loaned_out' entry)
              const hasReturned = parsedTags.some((tag: any) =>
                typeof tag === 'object' && tag !== null && tag.status === 'returned'
              );
              if (!hasReturned) {
                is_loaned_out = parsedTags.includes('loaned_out') || parsedTags.some((tag: any) =>
                  (typeof tag === 'string' && tag === 'loaned_out') ||
                  (typeof tag === 'object' && tag !== null && tag.status === 'loaned_out')
                );
              }
            } else if (typeof parsedTags === 'object') {
              // Plain object — check status directly
              if (parsedTags.status === 'loaned_out' || parsedTags.status === 'loaned out') {
                is_loaned_out = true;
              }
              // Check for expected_return_date to determine if overdue
              if (is_loaned_out && parsedTags.expected_return_date) {
                const returnDate = new Date(parsedTags.expected_return_date);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                returnDate.setHours(0, 0, 0, 0);
                if (returnDate < today) {
                  is_overdue = true;
                }
              }
            }
          }
        } catch (e) {
          // If parsing fails, try simple string check
          if (typeof tags === 'string') {
            // Only mark loaned_out if there is no 'returned' status present in the string
            const hasReturned = tags.includes('"status":"returned"') || tags.includes('"status": "returned"');
            if (!hasReturned) {
              is_loaned_out = tags.includes('loaned_out') || tags.includes('"status":"loaned_out"');
            }
          }
        }

        return { is_loaned_out, is_overdue };
      };

      // Helper function to normalize is_out_of_action value
      const normalizeOutOfAction = (value: any): boolean => {
        if (value === true || value === 1 || value === '1' || value === 'true' || value === 'True') {
          return true;
        }
        return false;
      };

      // Format asset data
      let formattedAssets: Asset[] = [];
      if (response.data && Array.isArray(response.data)) {
        formattedAssets = response.data.map((asset: any) => {
          const loanStatus = parseLoanStatus(asset.tags);
          return {
            asset_id: asset.asset_id,
            asset_name: asset.asset_name,
            asset_type_id: asset.asset_type_id,
            asset_type_name: asset.asset_type_name || 'Unknown',
            serial_number: asset.serial_number || '',
            qr_code: asset.qr_code || '',
            third_party_qr: asset.third_party_qr || '',
            location_id: asset.location_id,
            location_name: asset.location_name || 'Unknown',
            manufacturer: asset.manufacturer || '',
            model: asset.model || '',
            purchase_date: asset.purchase_date || '',
            photo_url: asset.photo_url || '',
            number_plate: asset.number_plate || '',
            description: asset.description || '',
            status: asset.status || 'active',
            tags: asset.tags,
            job_tags: Array.isArray(asset.job_tags) ? asset.job_tags : [],
            recorded_hours: asset.recorded_hours,
            has_recorded_hours: asset.has_recorded_hours,
            is_out_of_action: normalizeOutOfAction(asset.is_out_of_action),
            out_of_action_note: asset.out_of_action_note || '',
            out_of_action_date: asset.out_of_action_date || '',
            locked_by_user_id: asset.locked_by_user_id || null,
            locked_by_name: asset.locked_by_name || null,
            locked_at: asset.locked_at || null,
            locked_check_id: asset.locked_check_id || null,
            enabled_for_offline: Boolean(asset.enabled_for_offline === true || asset.enabled_for_offline === 1 || asset.enabled_for_offline === 'true'),
            mot_status: asset.mot_status || null,
            mot_due_date: asset.mot_due_date || null,
            mot_last_checked_at: asset.mot_last_checked_at || null,
            tax_status: asset.tax_status || null,
            tax_due_date: asset.tax_due_date || null,
            tax_last_checked_at: asset.tax_last_checked_at || null,
            vehicle_make: asset.vehicle_make || null,
            vehicle_model: asset.vehicle_model || null,
            vehicle_colour: asset.vehicle_colour || null,
            vehicle_fuel_type: asset.vehicle_fuel_type || null,
            vehicle_year: asset.vehicle_year || null,
            primary_user_id: asset.primary_user_id || null,
            primary_user_name: asset.primary_user_name || null,
            primary_member_id: asset.primary_member_id || null,
            primary_member_name: asset.primary_member_name || null,
            last_check_date: asset.last_check_date || '',
            ...loanStatus
          };
        });
      } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        // Handle the new API format if response is nested
        formattedAssets = response.data.data.map((asset: any) => {
          const loanStatus = parseLoanStatus(asset.tags);
          return {
            asset_id: asset.asset_id,
            asset_name: asset.asset_name,
            asset_type_id: asset.asset_type_id,
            asset_type_name: asset.asset_type_name || 'Unknown',
            serial_number: asset.serial_number || '',
            qr_code: asset.qr_code || '',
            third_party_qr: asset.third_party_qr || '',
            location_id: asset.location_id,
            location_name: asset.location_name || 'Unknown',
            manufacturer: asset.manufacturer || '',
            model: asset.model || '',
            purchase_date: asset.purchase_date || '',
            photo_url: asset.photo_url || '',
            number_plate: asset.number_plate || '',
            description: asset.description || '',
            status: asset.status || 'active',
            tags: asset.tags,
            job_tags: Array.isArray(asset.job_tags) ? asset.job_tags : [],
            recorded_hours: asset.recorded_hours,
            has_recorded_hours: asset.has_recorded_hours,
            is_out_of_action: normalizeOutOfAction(asset.is_out_of_action),
            out_of_action_note: asset.out_of_action_note || '',
            out_of_action_date: asset.out_of_action_date || '',
            locked_by_user_id: asset.locked_by_user_id || null,
            locked_by_name: asset.locked_by_name || null,
            locked_at: asset.locked_at || null,
            locked_check_id: asset.locked_check_id || null,
            enabled_for_offline: Boolean(asset.enabled_for_offline === true || asset.enabled_for_offline === 1 || asset.enabled_for_offline === 'true'),
            mot_status: asset.mot_status || null,
            mot_due_date: asset.mot_due_date || null,
            mot_last_checked_at: asset.mot_last_checked_at || null,
            tax_status: asset.tax_status || null,
            tax_due_date: asset.tax_due_date || null,
            tax_last_checked_at: asset.tax_last_checked_at || null,
            vehicle_make: asset.vehicle_make || null,
            vehicle_model: asset.vehicle_model || null,
            vehicle_colour: asset.vehicle_colour || null,
            vehicle_fuel_type: asset.vehicle_fuel_type || null,
            vehicle_year: asset.vehicle_year || null,
            primary_user_id: asset.primary_user_id || null,
            primary_user_name: asset.primary_user_name || null,
            primary_member_id: asset.primary_member_id || null,
            primary_member_name: asset.primary_member_name || null,
            last_check_date: asset.last_check_date || '',
            ...loanStatus
          };
        });
      }

      // last_check_date is now included in the assets API response directly.
      // Overdue detection for loaned-out assets is derived from the tags field
      // already parsed above in parseLoanStatus — no extra API calls needed.

      setAssets(formattedAssets);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setError('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestAssetLocations = async () => {
    try {
      setLoadingMap(true);
      logger.debug('🔄 Refreshing map data...');
      const token = localStorage.getItem('token');
      // Add cache-busting timestamp to force fresh data
      const timestamp = new Date().getTime();
      const res = await axios.get(`${API_BASE_URL}/assets/latest-locations?t=${timestamp}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      let data = [];
      if (res.data?.success && Array.isArray(res.data.data)) {
        data = res.data.data;
      } else if (Array.isArray(res.data)) {
        data = res.data;
      }
      
      logger.debug(`✅ Map data refreshed: ${data.length} assets loaded`);
      setMapData(data);
      
      // Keep loading state visible for a short time to ensure smooth rendering
      // This prevents the "flash" of the map before markers appear
      setTimeout(() => {
        setLoadingMap(false);
      }, 300);
    } catch (e) {
      console.error('Failed to load latest asset locations', e);
      setMapData([]);
      setLoadingMap(false);
    }
  };

  const fetchJobTags = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/job-tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setJobTags(data.filter((t: JobTag) => t.is_active !== false));
    } catch (err) {
      console.error('Failed to fetch job tags:', err);
    }
  };

  useEffect(() => {
    fetchLocations();
    fetchAssetTypes();
    fetchAssets();
    fetchJobTags();
    fetchTabPermissions();
  }, []);

  useEffect(() => {
    if (searchParams.get('tab') === 'locations') {
      navigate('/admin/locations', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!allowKeyDocuments && activeTab === 'files') {
      setActiveTab('assets');
      setSearchParams({}, { replace: true });
    }
  }, [allowKeyDocuments, activeTab, setSearchParams]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!tab) return;
    if (!ASSETS_PAGE_TABS.includes(tab as AssetsPageTab)) return;
    if (tab === 'files' && !allowKeyDocuments) {
      setActiveTab('assets');
      setSearchParams({}, { replace: true });
      return;
    }
    setActiveTab(tab as AssetsPageTab);
  }, [searchParams, allowKeyDocuments, setSearchParams]);

  const handleAssetsTabChange = (_: React.SyntheticEvent, newValue: AssetsPageTab) => {
    setActiveTab(newValue);
    if (newValue === 'assets') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: newValue }, { replace: true });
    }
  };

  const fetchTabPermissions = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      if (!token || !businessId) return true;
      const response = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = response.data;
      setAllowMotTaxTab(d.allow_mot_tax_tab === 1 || d.allow_mot_tax_tab === true);
      const keyDocsEnabled =
        d.allow_key_documents == null
          ? true
          : d.allow_key_documents === 1 || d.allow_key_documents === true;
      setAllowKeyDocuments(keyDocsEnabled);
      return keyDocsEnabled;
    } catch (err) {
      console.error('Failed to fetch tab permissions:', err);
      return true;
    }
  };

  // Default tag filter to "my job role(s)" when the user has job tags; respect sessionStorage if they already chose
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const { data } = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const ids = (data?.jobTags || []).map((t: { id: string }) => String(t.id));
        setUserJobTagIds(ids);
        if (data?.id) setCurrentUserId(String(data.id));

        setAssetFilters((prev) => {
          const jobTagKey = getAssetsJobTagFilterKey();
          const stored = sessionStorage.getItem(jobTagKey);
          if (ids.length === 0) {
            if (stored === 'my_tags') {
              sessionStorage.setItem(jobTagKey, 'all');
              return { ...prev, jobTag: 'all' };
            }
            if (stored != null && stored !== 'my_tags') {
              return { ...prev, jobTag: stored };
            }
            return prev;
          }
          if (stored != null) {
            if (stored === 'my_tags' || stored === 'all') {
              return { ...prev, jobTag: stored as 'all' | 'my_tags' };
            }
            return { ...prev, jobTag: stored };
          }
          return { ...prev, jobTag: 'all' };
        });
      } catch (e) {
        logger.debug('Could not load /auth/me for tag filter', e);
      }
    })();
  }, []);

  // Load a bespoke asset view when rendered as its own page (/admin/asset-views/:viewId)
  useEffect(() => {
    if (!standaloneViewId) return;
    let cancelled = false;
    (async () => {
      setStandaloneViewLoading(true);
      setStandaloneViewError(null);
      try {
        const token = localStorage.getItem('token');
        const businessId = localStorage.getItem('businessId');
        if (!token || !businessId) throw new Error('Not authenticated');
        const { data } = await axios.get(`${API_BASE_URL}/businesses/${businessId}/asset-views`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const views: BusinessAssetView[] = Array.isArray(data.asset_views) ? data.asset_views : [];
        const view = views.find(v => v.id === standaloneViewId);
        if (cancelled) return;
        if (!view) {
          setStandaloneView(null);
          setStandaloneViewError('This asset view could not be found.');
          return;
        }
        setStandaloneView(view);
        const { filters, searchQuery } = filtersFromAssetView(view);
        setAssetFilters(filters);
        setAssetSearchQuery(searchQuery);
      } catch (e: unknown) {
        if (!cancelled) {
          setStandaloneViewError(e instanceof Error ? e.message : 'Failed to load asset view');
        }
      } finally {
        if (!cancelled) setStandaloneViewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [standaloneViewId]);

  // Apply filters from navigation state (when coming from dashboard) or open asset for editing
  useEffect(() => {
    const state = location.state as { 
      applyFilter?: { loanStatus?: string; status?: string; assetType?: string; location?: string; outOfAction?: string };
      editAssetId?: string;
      openOutOfActionTab?: boolean;
    };
    
    if (state?.editAssetId) {
      // Find the asset and open it for editing
      const assetToEdit = assets.find(a => a.asset_id === state.editAssetId);
      if (assetToEdit) {
        handleOpenDialog(assetToEdit);
        // Switch to Out of Action Audit tab if requested
        if (state.openOutOfActionTab) {
          setActiveTab('audit');
        }
        // Clear the navigation state
        window.history.replaceState({}, document.title);
      } else {
        // Asset not found in current list, fetch it
        const fetchAssetForEdit = async () => {
          try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await axios.get(`${API_BASE_URL}/assets/${state.editAssetId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const assetData = response.data?.asset || response.data?.data;
            if (response.data?.success && assetData) {
              handleOpenDialog(assetData);
              if (state.openOutOfActionTab) {
                setActiveTab('audit');
              }
            }
          } catch (error) {
            console.error('Failed to fetch asset for editing:', error);
            setSnackbar({
              open: true,
              message: 'Failed to load asset for editing',
              severity: 'error'
            });
          }
          // Clear the navigation state
          window.history.replaceState({}, document.title);
        };
        fetchAssetForEdit();
      }
    } else if (state?.applyFilter) {
      const { loanStatus, status, assetType, location: filterLocation, outOfAction } = state.applyFilter;
      
      // Apply the filters from navigation
      setAssetFilters(prev => ({
        ...prev,
        ...(loanStatus && { loanStatus }),
        ...(status && { status }),
        ...(assetType && { assetType }),
        ...(filterLocation && { location: filterLocation }),
        ...(outOfAction && { outOfAction })
      }));

      // Clear the navigation state to prevent re-applying on subsequent renders
      window.history.replaceState({}, document.title);

      // Show a snackbar to inform the user
      const filterName = loanStatus === 'loaned_out' ? 'Loaned Out Assets' :
                         loanStatus === 'overdue' ? 'Overdue Assets' :
                         status === 'active' ? 'Active Assets' :
                         outOfAction === 'true' ? 'Out of Action Assets' :
                         'Filtered Assets';
      
      setSnackbar({
        open: true,
        message: `Showing ${filterName}`,
        severity: 'success'
      });
    }
    // Intentionally only run when location.state or assets change; handleOpenDialog is stable for this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, assets]);

  // Fetch custom fields when asset type changes in the dialog (for new assets)
  useEffect(() => {
    if (openDialog && formData.asset_type_id && !editingAsset) {
      // Only fetch if it's a new asset (not editing)
      fetchCustomFields(formData.asset_type_id);
    }
  }, [formData.asset_type_id, openDialog, editingAsset]);

  // Map lazy-load when tab is opened
  useEffect(() => {
    if (activeTab === 'map') {
      fetchLatestAssetLocations();
    }
  }, [activeTab]);

  // Fetch audit logs when audit tab is opened
  const fetchAuditLogs = async () => {
    try {
      setLoadingAuditLogs(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`${API_BASE_URL}/assets/out-of-action-audit`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setAuditLogs(response.data.data || []);
      } else {
        setError('Failed to fetch audit logs');
      }
    } catch (error: any) {
      console.error('Error fetching audit logs:', error);
      setError(error.response?.data?.message || 'Failed to fetch audit logs');
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs();
    }
  }, [activeTab]);

  const fetchBusinessUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_BASE_URL}/users?includeSharedAccountMembers=1`, { headers: { Authorization: `Bearer ${token}` } });
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setBusinessUsers(list);
    } catch (e) {
      console.error('Failed to load business users', e);
    }
  };

  const getImageUrl = (url: string) => {
    // Bunny or local handling; keep existing logic
    try {
      if (!url) return placeholderImage;
      if (url.startsWith('http')) return url;
      const baseUrl = API_BASE_URL.replace('/api', '');
      if (url.startsWith('/')) return `${baseUrl}${url}`;
      return `${baseUrl}/uploads/${url}`;
    } catch {
      return placeholderImage;
    }
  };

  // Function to handle image loading errors
  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    logger.debug('Image failed to load, using placeholder');
    event.currentTarget.src = placeholderImage;
    event.currentTarget.onerror = null; // Prevent infinite loop
  };

  // Dialog handlers for Asset
  const handleOpenDialog = async (asset?: Asset) => {
    const keyDocsEnabled = await fetchTabPermissions();
    if (asset) {
      logger.debug('📝 Editing asset:', {
        asset_id: asset.asset_id,
        asset_name: asset.asset_name,
        recorded_hours: asset.recorded_hours,
        recorded_hours_type: typeof asset.recorded_hours,
        is_out_of_action: asset.is_out_of_action,
        out_of_action_note: asset.out_of_action_note,
        out_of_action_date: asset.out_of_action_date
      });
      
      // Find the latest asset data from the assets state to ensure we have the most recent values
      if (asset?.asset_id) {
        const assetId = asset.asset_id;
        const latestAsset = assets.find(a => a.asset_id === assetId);
        if (latestAsset) {
          logger.debug('📝 Found latest asset in state:', {
            is_out_of_action: latestAsset.is_out_of_action,
            out_of_action_note: latestAsset.out_of_action_note,
            out_of_action_date: latestAsset.out_of_action_date
          });
          asset = latestAsset;
        } else {
          console.warn('⚠️ Asset not found in current assets list, using passed asset data');
        }
      }
      
      setEditingAsset(asset);
      const recordedHoursValue = asset.recorded_hours !== null && asset.recorded_hours !== undefined 
        ? String(asset.recorded_hours) 
        : '';
      
      logger.debug('📝 Setting form data with out-of-action:', {
        is_out_of_action: asset.is_out_of_action,
        out_of_action_note: asset.out_of_action_note,
        out_of_action_date: asset.out_of_action_date
      });
      
      setFormData({
        asset_id: asset.asset_id,
        asset_name: asset.asset_name,
        asset_type_id: asset.asset_type_id,
        serial_number: asset.serial_number || '',
        qr_code: asset.qr_code || '',
        third_party_qr: asset.third_party_qr || '',
        location_id: asset.location_id,
        manufacturer: asset.manufacturer || '',
        model: asset.model || '',
        purchase_date: asset.purchase_date ? asset.purchase_date.split('T')[0] : '',
        photo_url: asset.photo_url || '',
        number_plate: asset.number_plate || '',
        description: asset.description || '',
        recorded_hours: recordedHoursValue,
        is_out_of_action: Boolean(asset.is_out_of_action),
        out_of_action_note: asset.out_of_action_note || '',
        enabled_for_offline: Boolean(asset.enabled_for_offline),
        job_tag_ids: (asset.job_tags || []).map((t: JobTag) => String(t.job_tag_id)),
        primary_user_id: asset.primary_member_id ? null : (asset.primary_user_id || null),
        primary_member_id: asset.primary_member_id || null,
        primary_member_name: asset.primary_member_name || null,
      });
      // Fetch custom fields with values for editing
      await fetchAssetCustomFields(asset.asset_id, asset.asset_type_id);
      // Fetch existing attachments
      await fetchAssetAttachments(asset.asset_id);
      // Fetch key documents (when feature enabled)
      if (keyDocsEnabled) await fetchAssetDocuments(asset.asset_id);
    } else {
      setEditingAsset(null);
      setFormData({
        asset_name: '',
        asset_type_id: '',
        serial_number: '',
        qr_code: '',
        third_party_qr: '',
        location_id: '',
        manufacturer: '',
        model: '',
        purchase_date: '',
        photo_url: '',
        number_plate: '',
        description: '',
        recorded_hours: '',
        is_out_of_action: false,
        out_of_action_note: '',
        enabled_for_offline: false,
        job_tag_ids: [],
        primary_user_id: null,
        primary_member_id: null,
        primary_member_name: null,
      });
      setCustomFields([]);
      setCustomFieldValues({});
      setAttachments([]);
      setAssetDocuments([]);
      setDocFolders([]);
      setAiAnalysis(null);
    }
    fetchBusinessUsers();
    setAssetDialogTab('details');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setAssetDialogTab('details');
    setEditingAsset(null);
    // Reset form data when closing dialog
    setFormData({
      asset_name: '',
      asset_type_id: '',
      serial_number: '',
      qr_code: '',
      third_party_qr: '',
      location_id: '',
      manufacturer: '',
      model: '',
      purchase_date: '',
      photo_url: '',
      number_plate: '',
      description: '',
      recorded_hours: '',
      is_out_of_action: false,
      out_of_action_note: '',
      enabled_for_offline: false,
      job_tag_ids: [],
      primary_user_id: null,
      primary_member_id: null,
      primary_member_name: null,
    });
    setCustomFields([]);
    setCustomFieldValues({});
    setAttachments([]);
    setAssetDocuments([]);
    setDocFolders([]);
    setAiAnalysis(null);
    setDocReviews({});
    setDocReviewLoading({});
    setTextEditorOpen(false);
    setTextEditorDoc(null);
    setTextEditorContent('');
  };

  // Location history handlers
  const handleShowLocationHistory = async (assetId: string, assetName: string) => {
    setLocationHistoryDialog({ asset_id: assetId, asset_name: assetName });
    setLoadingLocationHistory(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`${API_BASE_URL}/assets/${assetId}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success && Array.isArray(response.data.data)) {
        setLocationHistory(response.data.data);
      } else if (Array.isArray(response.data)) {
        setLocationHistory(response.data);
      } else {
        setLocationHistory([]);
      }
    } catch (error) {
      console.error('Error fetching location history:', error);
      setError('Failed to fetch location history');
      setLocationHistory([]);
    } finally {
      setLoadingLocationHistory(false);
    }
  };

  const handleCloseLocationHistoryDialog = () => {
    setLocationHistoryDialog(null);
    setLocationHistory([]);
  };

  // Fetch all historical locations for map display
  const fetchAllLocationHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`${API_BASE_URL}/assets/locations/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success && Array.isArray(response.data.data)) {
        setHistoricalLocations(response.data.data);
      } else {
        setHistoricalLocations([]);
      }
    } catch (error) {
      console.error('Error fetching all location history:', error);
      setHistoricalLocations([]);
    }
  };

  // Toggle location history display
  const toggleLocationHistory = () => {
    if (!showLocationHistory) {
      fetchAllLocationHistory();
    }
    setShowLocationHistory(!showLocationHistory);
  };

  // Asset create/update submit
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Normalize date
      let formattedPurchaseDate: string | null = null;
      if (formData.purchase_date) {
        const d = new Date(formData.purchase_date);
        if (!isNaN(d.getTime())) formattedPurchaseDate = d.toISOString().split('T')[0];
      }

      // Prepare custom field values for submission
      const customFieldValuesArray = customFields.map(field => ({
        custom_field_id: field.custom_field_id,
        field_value: customFieldValues[field.custom_field_id] || ''
      }));

      // Check if selected asset type has recorded hours enabled
      const selectedAssetType = assetTypes.find(at => at.asset_type_id === formData.asset_type_id);
      const hasRecordedHours = selectedAssetType?.has_recorded_hours;

      // Process recorded_hours value
      let recordedHoursValue = null;
      if (hasRecordedHours && formData.recorded_hours && formData.recorded_hours !== '') {
        const parsedValue = parseFloat(formData.recorded_hours);
        recordedHoursValue = !isNaN(parsedValue) ? parsedValue : null;
      }

      logger.debug('📝 Submitting asset with recorded_hours:', {
        hasRecordedHours,
        formData_recorded_hours: formData.recorded_hours,
        recordedHoursValue,
        asset_type_id: formData.asset_type_id
      });

      // Ensure boolean values are properly converted
      const isOutOfAction = Boolean(formData.is_out_of_action);
      
      logger.debug('📝 Out of action values:', {
        formData_is_out_of_action: formData.is_out_of_action,
        formData_is_out_of_action_type: typeof formData.is_out_of_action,
        isOutOfAction,
        out_of_action_note: formData.out_of_action_note
      });

      const payload = {
        asset_name: formData.asset_name,
        asset_type_id: formData.asset_type_id,
        location_id: formData.location_id,
        serial_number: formData.serial_number || null,
        qr_code: formData.qr_code || null,
        third_party_qr: formData.third_party_qr || null,
        manufacturer: formData.manufacturer || null,
        model: formData.model || null,
        purchase_date: formattedPurchaseDate,
        number_plate: formData.number_plate || null,
        description: formData.description || null,
        photo_url: formData.photo_url || null,
        status: editingAsset?.status || 'active',
        custom_field_values: customFieldValuesArray,
        recorded_hours: recordedHoursValue,
        is_out_of_action: isOutOfAction,
        out_of_action_note: formData.out_of_action_note && formData.out_of_action_note.trim() !== '' ? formData.out_of_action_note.trim() : null,
        enabled_for_offline: Boolean(formData.enabled_for_offline),
        job_tag_ids: formData.job_tag_ids || [],
        primary_user_id: formData.primary_member_id ? null : (formData.primary_user_id || null),
        primary_member_id: formData.primary_member_id || null,
        primary_member_name: formData.primary_member_name || null,
      };

      const isUpdating = Boolean(formData.asset_id);
      const url = isUpdating
        ? `${API_BASE_URL}/assets/${formData.asset_id}`
        : `${API_BASE_URL}/assets`;
      const method = isUpdating ? 'PUT' : 'POST';

      const response = await axios.request({
        method,
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: payload
      });

      logger.debug('✅ Asset saved successfully:', response.data);
      logger.debug('📝 Payload sent:', payload);
      logger.debug('📝 Form data:', formData);

      setSnackbar({ open: true, message: isUpdating ? 'Asset updated' : 'Asset created', severity: 'success' });
      
      // Refresh assets list to get updated data
      await fetchAssets();
      
      // If updating, also update the editingAsset in state with the new values
      if (isUpdating && editingAsset) {
        const updatedAsset: Asset = {
          ...editingAsset,
          is_out_of_action: isOutOfAction,
          out_of_action_note: formData.out_of_action_note && formData.out_of_action_note.trim() !== '' ? formData.out_of_action_note.trim() : undefined,
          out_of_action_date: isOutOfAction && !editingAsset.is_out_of_action ? new Date().toISOString() : editingAsset.out_of_action_date
        };
        setEditingAsset(updatedAsset);
        logger.debug('📝 Updated editingAsset with out-of-action data:', updatedAsset);
      }
      
      setOpenDialog(false);
    } catch (e: any) {
      console.error('Error saving asset', e);
      setSnackbar({ open: true, message: 'Failed to save asset', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // QR Code handlers
  const handleOpenQrDialog = (asset: Asset) => {
    const useQrCode = Boolean(asset.qr_code);
    const identifier = asset.qr_code || asset.serial_number;
    if (!identifier) {
      setSnackbar({ open: true, message: 'Asset has no QR code or serial number set', severity: 'error' });
      return;
    }
    // Generate HTTP URL that redirects to deep link
    // This enables native camera apps (iOS Camera, Android Camera) to recognize and open the link
    // Web admin will redirect: https://app.safetychecknow.com/asset/IDENTIFIER → checknow://asset/IDENTIFIER
    const webAdminUrl = window.location.origin;
    const qrCodeUrl = `${webAdminUrl}/asset/${encodeURIComponent(String(identifier))}`;
    setQrData({
      assetName: asset.asset_name,
      url: qrCodeUrl,
      identifierLabel: useQrCode ? 'QR Code' : 'Serial Number',
      identifierValue: String(identifier),
    });
    setQrDialogOpen(true);
  };

  const handleCloseQrDialog = () => {
    setQrDialogOpen(false);
    setQrData(null);
  };

  const handleOutOfActionSave = async () => {
    if (!outOfActionDialog.asset) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const isOutOfAction = !outOfActionDialog.isOutOfAction; // Toggle the status
      const note = outOfActionDialog.note || '';

      // Require a note when marking out of action
      if (isOutOfAction && !note.trim()) {
        setSnackbar({
          open: true,
          message: 'Please provide a note explaining why this asset is out of action',
          severity: 'error'
        });
        return;
      }

      // When marking back as available, require a note
      if (!isOutOfAction && outOfActionDialog.isOutOfAction && !note.trim()) {
        setSnackbar({
          open: true,
          message: 'Please provide a note explaining why the asset is now available',
          severity: 'error'
        });
        return;
      }

      // Upload images if any
      let imageUrls: string[] = [];
      if (outOfActionDialog.images.length > 0) {
        setOutOfActionDialog({ ...outOfActionDialog, uploadingImages: true });
        
        for (const image of outOfActionDialog.images) {
          const formData = new FormData();
          formData.append('photo', image);

          const uploadResponse = await axios.post(
            `${API_BASE_URL}/upload`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (uploadResponse.data.success && uploadResponse.data.photoUrl) {
            imageUrls.push(uploadResponse.data.photoUrl);
          }
        }

        setOutOfActionDialog({ ...outOfActionDialog, uploadingImages: false });
      }

      const response = await axios.put(
        `${API_BASE_URL}/assets/${outOfActionDialog.asset.asset_id}/out-of-action`,
        {
          is_out_of_action: isOutOfAction,
          out_of_action_note: note,
          image_urls: imageUrls.length > 0 ? imageUrls : undefined
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: isOutOfAction 
            ? `Asset "${outOfActionDialog.asset.asset_name}" marked as out of action` 
            : `Asset "${outOfActionDialog.asset.asset_name}" marked as available`,
          severity: 'success'
        });
        setOutOfActionDialog({ open: false, asset: null, note: '', isOutOfAction: false, images: [], uploadingImages: false });
        fetchAssets(); // Refresh the asset list
      }
    } catch (error: any) {
      console.error('Error updating out of action status:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update asset status',
        severity: 'error'
      });
      setOutOfActionDialog({ ...outOfActionDialog, uploadingImages: false });
    }
  };

  const handleDownloadQr = () => {
    try {
      const container = document.getElementById('qr-code-container');
      if (!container) return;
      const svg = container.querySelector('svg');
      if (!svg) return;
      const serializer = new XMLSerializer();
      const source = serializer.serializeToString(svg);
      const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = (qrData?.identifierValue || qrData?.assetName || 'asset').replace(/[^a-z0-9-_]/gi, '_');
      link.href = url;
      link.download = `${safeName}_QR.svg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to download QR', e);
      setSnackbar({ open: true, message: 'Failed to download QR code', severity: 'error' });
    }
  };

  const handlePrintQr = () => {
    try {
      const container = document.getElementById('qr-code-container');
      if (!container || !qrData) return;
      const svg = container.querySelector('svg');
      if (!svg) return;

      const serializer = new XMLSerializer();
      const svgMarkup = serializer.serializeToString(svg);
      const logoSrc = safetyLogo; // asset path handled by bundler

      const titleText = 'Check Now';
      const assetName = qrData.assetName;
      const identifierLabel = qrData.identifierLabel;
      const identifierValue = qrData.identifierValue;
      const generatedAt = new Date().toLocaleString();

      const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${titleText} — Asset QR</title>
    <style>
      @page { size: A4; margin: 16mm; }
      html, body { height: 100%; }
      body { font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif; margin: 0; color: #111827; }
      .wrap { display: flex; flex-direction: column; align-items: center; justify-content: flex-start; padding: 24px; }
      .brand { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
      .brand img { width: 48px; height: 48px; object-fit: contain; }
      .brand .title { font-size: 24px; font-weight: 800; letter-spacing: 0.3px; }
      .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 24px; max-width: 560px; width: 100%; text-align: center; }
      .asset { margin-top: 8px; margin-bottom: 16px; }
      .asset .name { font-size: 18px; font-weight: 700; }
      .asset .serial { font-size: 14px; color: #6b7280; margin-top: 4px; }
      .instructions { margin-top: 16px; padding: 12px; background: #f3f4f6; border-radius: 8px; font-size: 12px; color: #4b5563; text-align: left; }
      .instructions strong { color: #111827; }
      .instructions ol { margin: 8px 0 0 20px; padding: 0; }
      .instructions li { margin: 4px 0; }
      .qr { background: #fff; display: inline-block; padding: 16px; border-radius: 12px; border: 1px dashed #d1d5db; }
      .qr svg { width: 280px; height: 280px; display: block; }
      .meta { margin-top: 12px; font-size: 12px; color: #6b7280; }
      .footer { margin-top: 24px; font-size: 12px; color: #9ca3af; }
      @media print { .card { box-shadow: none; border-color: #d1d5db; } }
    </style>
  </head>
  <body>
    <div class="wrap">
      <div class="brand">
        <img src="${logoSrc}" alt="Check Now" />
        <div class="title">${titleText}</div>
      </div>
      <div class="card">
        <div class="asset">
          <div class="name">${assetName}</div>
          <div class="serial">${identifierLabel}: ${identifierValue}</div>
        </div>
        <div class="qr">${svgMarkup}</div>
        <div class="meta">Generated: ${generatedAt}</div>
      </div>
    </div>
  </body>
</html>`;

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;
      doc.open();
      doc.write(html);
      doc.close();

      const doPrint = () => {
        const win = iframe.contentWindow;
        if (!win) return;
        win.focus();
        setTimeout(() => {
          win.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 500);
        }, 200);
      };

      if (iframe.addEventListener) {
        iframe.addEventListener('load', doPrint);
      } else {
        setTimeout(doPrint, 400);
      }
    } catch (e) {
      console.error('Failed to open print view', e);
      setSnackbar({ open: true, message: 'Failed to open print view', severity: 'error' });
    }
  };

  // Asset delete
  const handleDelete = async (assetId: string) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(prev => prev.filter(a => a.asset_id !== assetId));
      setSnackbar({ open: true, message: 'Asset deleted', severity: 'success' });
    } catch (e) {
      console.error('Delete asset failed', e);
      setSnackbar({ open: true, message: 'Failed to delete asset', severity: 'error' });
    }
  };

  const getMotStatusCategory = (dueDateStr?: string | null, status?: string | null): 'valid' | 'due_soon' | 'expired' | 'unknown' => {
    if (!status || status === 'No results returned' || status === 'No details held by DVLA') return 'unknown';
    const sl = status.toLowerCase();
    if (sl.includes('not valid') || sl === 'invalid') return 'expired';
    if (!dueDateStr) return 'unknown';
    const due = new Date(dueDateStr);
    const now = new Date();
    const days = Math.floor((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'expired';
    if (days <= 30) return 'due_soon';
    return 'valid';
  };

  const getTaxStatusCategory = (dueDateStr?: string | null, status?: string | null): 'valid' | 'due_soon' | 'expired' | 'unknown' => {
    if (!status) return 'unknown';
    const sl = status.toLowerCase();
    if (sl === 'untaxed' || sl === 'sorn') return 'expired';
    if (sl === 'taxed') {
      if (!dueDateStr) return 'valid';
      const due = new Date(dueDateStr);
      const now = new Date();
      const days = Math.floor((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (days < 0) return 'expired';
      if (days <= 30) return 'due_soon';
      return 'valid';
    }
    return 'unknown';
  };

  const openMotHistory = async (asset: Asset) => {
    setMotHistoryModal({ open: true, asset, history: [], loading: true, refreshing: false });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/assets/${asset.asset_id}/mot-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setMotHistoryModal(prev => ({
          ...prev,
          asset: { ...asset, ...res.data.data.asset },
          history: res.data.data.history,
          loading: false,
        }));
      } else {
        setMotHistoryModal(prev => ({ ...prev, loading: false }));
      }
    } catch {
      setMotHistoryModal(prev => ({ ...prev, loading: false }));
    }
  };

  const runMotCheckForAsset = async (asset: Asset) => {
    if (motCheckingIds.has(asset.asset_id)) return;
    setMotCheckingIds(prev => new Set(prev).add(asset.asset_id));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/assets/${asset.asset_id}/refresh-mot`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${API_BASE_URL}/assets/${asset.asset_id}/mot-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const updated = res.data.data.asset;
        setAssets(prev => prev.map(a => a.asset_id === asset.asset_id ? { ...a, ...updated } : a));
        setSnackbar({ open: true, message: `MOT check complete for ${asset.asset_name}`, severity: 'success' });
      }
    } catch {
      setSnackbar({ open: true, message: `Failed to run MOT check for ${asset.asset_name}`, severity: 'error' });
    } finally {
      setMotCheckingIds(prev => { const s = new Set(prev); s.delete(asset.asset_id); return s; });
    }
  };

  const refreshMotCheck = async (assetId: string) => {
    setMotHistoryModal(prev => ({ ...prev, refreshing: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/assets/${assetId}/refresh-mot`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Re-fetch history and updated asset data
      const res = await axios.get(`${API_BASE_URL}/assets/${assetId}/mot-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const updatedAsset = res.data.data.asset;
        setMotHistoryModal(prev => ({
          ...prev,
          asset: { ...(prev.asset as Asset), ...updatedAsset },
          history: res.data.data.history,
          refreshing: false,
        }));
        // Update asset in the main list
        setAssets(prev => prev.map(a => a.asset_id === assetId ? { ...a, ...updatedAsset } : a));
        setSnackbar({ open: true, message: 'MOT data refreshed', severity: 'success' });
      }
    } catch {
      setMotHistoryModal(prev => ({ ...prev, refreshing: false }));
      setSnackbar({ open: true, message: 'Failed to refresh MOT data', severity: 'error' });
    }
  };

  const handleOpenUnlockDialog = async (asset: Asset) => {
    setUnlockDialog({ open: true, asset, checkResponses: [], loadingResponses: true, unlocking: false });
    if (!asset.locked_check_id) {
      setUnlockDialog(prev => ({ ...prev, loadingResponses: false }));
      return;
    }
    try {
      const token = localStorage.getItem('token');

      // Try the individual check responses endpoint first
      let responsesArr: any[] = [];
      try {
        const res = await axios.get(`${API_BASE_URL}/safety-checks/${asset.locked_check_id}/responses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Endpoint returns { success, data: [...] }
        responsesArr = Array.isArray(res.data?.data) ? res.data.data : [];
      } catch (innerErr: any) {
        console.warn('Could not fetch via /responses, trying check-responses endpoint:', innerErr?.response?.status, innerErr?.message);
        // Fallback: use the check-responses listing endpoint with the specific check ID
        const res2 = await axios.get(`${API_BASE_URL}/check-responses`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { safety_check_id: asset.locked_check_id, include_discarded: 'true' }
        });
        const groups: any[] = Array.isArray(res2.data?.data) ? res2.data.data : [];
        const group = groups.find((g: any) => g.safety_check_id === asset.locked_check_id);
        responsesArr = group?.responses || [];
      }

      // Filter out empty placeholder rows — keep any row with actual response text
      const answeredResponses = responsesArr.filter(
        (r: any) => r.response_text != null && r.response_text !== '' && r.response_text !== 'No response provided'
      );
      setUnlockDialog(prev => ({ ...prev, checkResponses: answeredResponses, loadingResponses: false }));
    } catch (err: any) {
      console.error('Failed to load check responses for unlock dialog:', err?.response?.status, err?.message);
      setUnlockDialog(prev => ({ ...prev, checkResponses: [], loadingResponses: false }));
    }
  };

  const handleUnlockAsset = async () => {
    const assetToUnlock = unlockDialog.asset;
    if (!assetToUnlock) return;
    setUnlockDialog(prev => ({ ...prev, unlocking: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/assets/${assetToUnlock.asset_id}/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(prev => prev.map(a =>
        a.asset_id === assetToUnlock.asset_id
          ? { ...a, locked_by_user_id: null, locked_by_name: null, locked_at: null, locked_check_id: null }
          : a
      ));
      setUnlockDialog({ open: false, asset: null, checkResponses: [], loadingResponses: false, unlocking: false });
      setSnackbar({ open: true, message: 'Asset unlocked and check discarded', severity: 'success' });
    } catch {
      setUnlockDialog(prev => ({ ...prev, unlocking: false }));
      setSnackbar({ open: true, message: 'Failed to unlock asset', severity: 'error' });
    }
  };

  // Dialog handlers for Asset Type
  const handleOpenAssetTypeDialog = (assetType?: AssetType) => {
    if (assetType && assetType.is_global === 1) {
      setSnackbar({ open: true, message: 'Global asset types cannot be edited', severity: 'error' });
      return;
    }
    if (assetType) {
      setEditingAssetType(assetType);
      setAssetTypeFormData({
        type_name: assetType.type_name,
        description: assetType.description,
        requires_car_registration: assetType.requires_car_registration || false,
        has_whole_good_number: assetType.has_whole_good_number || false,
        has_recorded_hours: assetType.has_recorded_hours || false,
        mark_out_of_action_on_fail:
          assetType.mark_out_of_action_on_fail !== false &&
          assetType.mark_out_of_action_on_fail !== 0,
        allow_loan_attachments: !!(assetType.allow_loan_attachments === true || assetType.allow_loan_attachments === 1),
      });
    } else {
      setEditingAssetType(null);
      setAssetTypeFormData({ type_name: '', description: '', requires_car_registration: false, has_whole_good_number: false, has_recorded_hours: false, mark_out_of_action_on_fail: true, allow_loan_attachments: false });
    }
    setOpenAssetTypeDialog(true);
  };

  const handleCloseAssetTypeDialog = () => {
    setOpenAssetTypeDialog(false);
    setEditingAssetType(null);
  };

  const handleAssetTypeSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const isUpdating = Boolean(editingAssetType);
      const url = isUpdating
        ? `${API_BASE_URL}/asset-types/${editingAssetType!.asset_type_id}`
        : `${API_BASE_URL}/asset-types`;
      const method = isUpdating ? 'PUT' : 'POST';
      await axios.request({
        method,
        url,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: assetTypeFormData
      });
      setSnackbar({ open: true, message: isUpdating ? 'Asset type updated' : 'Asset type created', severity: 'success' });
      fetchAssetTypes();
      handleCloseAssetTypeDialog();
    } catch (e) {
      console.error('Save asset type failed', e);
      setSnackbar({ open: true, message: 'Failed to save asset type', severity: 'error' });
    }
  };

  const handleDeleteAssetType = async (assetTypeId: string, assetType: AssetType) => {
    if (assetType.is_global === 1) {
      setSnackbar({ open: true, message: 'Global asset types cannot be deleted', severity: 'error' });
      return;
    }
    if (!window.confirm('Delete this asset type?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/asset-types/${assetTypeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({ open: true, message: 'Asset type deleted', severity: 'success' });
      fetchAssetTypes();
    } catch (e) {
      console.error('Delete asset type failed', e);
      setSnackbar({ open: true, message: 'Failed to delete asset type', severity: 'error' });
    }
  };

  // Image upload
  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Auto-upload the file
      try {
        setUploadingImage(true);
        const token = localStorage.getItem('token');
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_BASE_URL}/upload`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        if (res.data?.success && res.data.filePath) {
          setFormData(prev => ({ ...prev, photo_url: res.data.filePath }));
          setSnackbar({ open: true, message: 'Image uploaded successfully', severity: 'success' });
          // Clear the file input so the same file can be selected again if needed
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else {
          throw new Error('Invalid upload response');
        }
      } catch (e) {
        console.error('Upload failed', e);
        setSnackbar({ open: true, message: 'Failed to upload image', severity: 'error' });
        // Clear the file input on error as well
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } finally {
        setUploadingImage(false);
      }
    }
  };

  // Fetch existing attachments for an asset
  const fetchAssetAttachments = async (assetId: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/assets/${assetId}/attachments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success && Array.isArray(res.data.attachments)) {
        setAttachments(res.data.attachments);
      }
    } catch (e) {
      console.warn('Could not load asset attachments:', e);
      setAttachments([]);
    }
  };

  // ── Key Documents helpers ────────────────────────────────────────────────────

  const fetchAssetDocuments = async (assetId: string) => {
    setLoadingDocs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/assets/${assetId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        setAssetDocuments(res.data.documents || []);
        setDocFolders(res.data.folders || []);
      }
    } catch (e) {
      console.warn('Could not load asset documents:', e);
      setAssetDocuments([]);
      setDocFolders([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!editingAsset || !newDocForm.file || !newDocForm.title.trim()) return;
    setUploadingDoc(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', newDocForm.file);
      fd.append('document_type', newDocForm.document_type);
      fd.append('title', newDocForm.title.trim());
      if (newDocForm.folder_id) fd.append('folder_id', newDocForm.folder_id);
      if (newDocForm.issue_date) fd.append('issue_date', newDocForm.issue_date);
      if (newDocForm.expiry_date) fd.append('expiry_date', newDocForm.expiry_date);
      if (newDocForm.notes) fd.append('notes', newDocForm.notes);

      await axios.post(`${API_BASE_URL}/assets/${editingAsset.asset_id}/documents`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchAssetDocuments(editingAsset.asset_id);
      setAddDocDialogOpen(false);
      setNewDocForm({ document_type: 'other', title: '', folder_id: '', issue_date: '', expiry_date: '', notes: '', file: null });
      setSnackbar({ open: true, message: 'Document uploaded', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to upload document', severity: 'error' });
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (doc: any) => {
    if (!editingAsset) return;
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/assets/${editingAsset.asset_id}/documents/${doc.document_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssetDocuments(prev => prev.filter(d => d.document_id !== doc.document_id));
      setSnackbar({ open: true, message: 'Document deleted', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete document', severity: 'error' });
    }
  };

  const handleCreateFolder = async () => {
    if (!editingAsset || !newFolderName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/assets/${editingAsset.asset_id}/documents/folders`,
        { folder_name: newFolderName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setDocFolders(prev => [...prev, res.data.folder]);
        setNewFolderName('');
        setAddFolderDialogOpen(false);
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to create folder', severity: 'error' });
    }
  };

  const openRenameFolder = (folderId: string, folderName: string) => {
    setRenameTarget({ type: 'folder', id: folderId, name: folderName });
    setRenameName(folderName);
    setRenameDialogOpen(true);
  };

  const openRenameDocument = (doc: { document_id: string; title: string; file_name?: string }) => {
    setRenameTarget({ type: 'file', id: doc.document_id, name: doc.title });
    setRenameName(doc.title || doc.file_name || '');
    setRenameDialogOpen(true);
  };

  const handleSaveRename = async () => {
    if (!editingAsset || !renameTarget || !renameName.trim()) return;
    setRenameSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (renameTarget.type === 'folder') {
        await axios.put(
          `${API_BASE_URL}/assets/${editingAsset.asset_id}/documents/folders/${renameTarget.id}`,
          { folder_name: renameName.trim() },
          { headers }
        );
        setDocFolders(prev => prev.map(f => f.folder_id === renameTarget.id ? { ...f, folder_name: renameName.trim() } : f));
        setAssetDocuments(prev => prev.map(d => d.folder_id === renameTarget.id ? { ...d, folder_name: renameName.trim() } : d));
      } else {
        await axios.put(
          `${API_BASE_URL}/assets/${editingAsset.asset_id}/documents/${renameTarget.id}`,
          { title: renameName.trim() },
          { headers }
        );
        setAssetDocuments(prev => prev.map(d => d.document_id === renameTarget.id ? { ...d, title: renameName.trim() } : d));
      }
      setSnackbar({ open: true, message: 'Renamed', severity: 'success' });
      setRenameDialogOpen(false);
      setRenameTarget(null);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.message
        ? String(err.response.data.message)
        : 'Rename failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setRenameSaving(false);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!editingAsset) return;
    if (!window.confirm('Delete this folder? Documents inside will become unfoldered.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/assets/${editingAsset.asset_id}/documents/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocFolders(prev => prev.filter(f => f.folder_id !== folderId));
      setAssetDocuments(prev => prev.map(d => d.folder_id === folderId ? { ...d, folder_id: null, folder_name: undefined } : d));
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete folder', severity: 'error' });
    }
  };

  const handleRunAiAnalysis = async () => {
    if (!editingAsset) return;
    setAiAnalysisLoading(true);
    setAiAnalysis(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/assets/${editingAsset.asset_id}/documents/ai-analysis`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) setAiAnalysis(res.data.analysis);
    } catch (e) {
      setSnackbar({ open: true, message: 'AI analysis failed', severity: 'error' });
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handleOpenTextEditor = async (assetId: string, doc: any) => {
    setTextEditorDoc(doc);
    setTextEditorContent('');
    setTextEditorLoading(true);
    setTextEditorOpen(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE_URL}/assets/${assetId}/documents/${doc.document_id}/text`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTextEditorContent(res.data?.content || '');
    } catch {
      setSnackbar({ open: true, message: 'Could not load text content', severity: 'error' });
      setTextEditorOpen(false);
    } finally {
      setTextEditorLoading(false);
    }
  };

  const handleSaveTextEditor = async (assetId: string) => {
    if (!textEditorDoc) return;
    setTextEditorSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/assets/${assetId}/documents/${textEditorDoc.document_id}/text`,
        { content: textEditorContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: 'Document saved', severity: 'success' });
      setTextEditorOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    } finally {
      setTextEditorSaving(false);
    }
  };

  const handleCreateTextDoc = async (assetId: string) => {
    if (!newTextDocForm.title.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/assets/${assetId}/documents/text`,
        newTextDocForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTextDocDialogOpen(false);
      setNewTextDocForm({ document_type: 'other', title: '', folder_id: '', issue_date: '', expiry_date: '', notes: '', content: '' });
      await fetchAssetDocuments(assetId);
      setSnackbar({ open: true, message: 'Text document created', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Could not create document', severity: 'error' });
    }
  };

  const handleRunDocumentReview = async (assetId: string, doc: any) => {    setDocReviewLoading(prev => ({ ...prev, [doc.document_id]: true }));
    setDocReviews(prev => ({ ...prev, [doc.document_id]: null }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/assets/${assetId}/documents/${doc.document_id}/review`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setDocReviews(prev => ({ ...prev, [doc.document_id]: res.data.review }));
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Document review failed', severity: 'error' });
    } finally {
      setDocReviewLoading(prev => ({ ...prev, [doc.document_id]: false }));
    }
  };

  const docTypeOptions = [
    { value: 'loler',            label: 'LOLER Certificate' },
    { value: 'puwer',            label: 'PUWER Assessment' },
    { value: 'insurance',        label: 'Insurance Certificate' },
    { value: 'service_record',   label: 'Service Record' },
    { value: 'rams',             label: 'RAMS' },
    { value: 'method_statement', label: 'Method Statement' },
    { value: 'operator_licence', label: 'Operator Licence' },
    { value: 'pre_use_record',   label: 'Pre-use Inspection Record' },
    { value: 'other',            label: 'Other' },
  ];

  const docTypeLabel = (type: string) =>
    docTypeOptions.find(o => o.value === type)?.label || type;

  const docExpiryStatus = (expiry?: string): 'ok' | 'soon' | 'expired' | 'none' => {
    if (!expiry) return 'none';
    const diff = (new Date(expiry).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    if (diff < 0) return 'expired';
    if (diff <= 30) return 'soon';
    return 'ok';
  };

  const expiryChipProps = (status: ReturnType<typeof docExpiryStatus>, expiry?: string) => {
    const dateStr = expiry ? new Date(expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    if (status === 'expired') return { label: `Expired ${dateStr}`, color: 'error' as const };
    if (status === 'soon')    return { label: `Exp ${dateStr}`, color: 'warning' as const };
    if (status === 'ok')      return { label: `Valid to ${dateStr}`, color: 'success' as const };
    return { label: 'No expiry', color: 'default' as const };
  };

  // Upload a new attachment for an existing asset
  const handleAttachmentFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!editingAsset) {
      setSnackbar({ open: true, message: 'Please save the asset first before adding attachments', severity: 'error' });
      return;
    }

    const token = localStorage.getItem('token');
    setUploadingAttachment(true);

    for (const file of Array.from(files)) {
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const fileType = file.type.startsWith('image/') ? 'image' : (file.type === 'application/pdf' ? 'pdf' : 'document');
      setAttachments(prev => [...prev, {
        attachment_id: tempId,
        file_url: URL.createObjectURL(file),
        file_name: file.name,
        file_type: fileType as AssetAttachment['file_type'],
        mime_type: file.type,
        file_size: file.size,
        display_order: attachments.length,
      }]);

      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('display_order', String(attachments.length));
        const res = await axios.post(
          `${API_BASE_URL}/assets/${editingAsset.asset_id}/attachments`,
          fd,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        if (res.data?.success && res.data.attachment) {
          const saved: AssetAttachment = res.data.attachment;
          setAttachments(prev => {
            const list = prev.filter(a => a.attachment_id !== tempId);
            return [...list, saved];
          });
          // Keep photo_url pointing at first image for backward compat
          if (!formData.photo_url && saved.file_type === 'image') {
            setFormData(prev => ({ ...prev, photo_url: saved.file_url }));
          }
          setSnackbar({ open: true, message: `"${file.name}" uploaded`, severity: 'success' });
        } else {
          throw new Error('Invalid response');
        }
      } catch (e) {
        setAttachments(prev => prev.filter(a => a.attachment_id !== tempId));
        setSnackbar({ open: true, message: `Failed to upload "${file.name}"`, severity: 'error' });
      }
    }

    setUploadingAttachment(false);
    if (attachmentFileInputRef.current) attachmentFileInputRef.current.value = '';
  };

  const handleDeleteAttachment = async (attachment: AssetAttachment) => {
    if (!editingAsset) return;
    if (!window.confirm(`Remove "${attachment.file_name}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/assets/${editingAsset.asset_id}/attachments/${attachment.attachment_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttachments(prev => prev.filter(a => a.attachment_id !== attachment.attachment_id));
      if (formData.photo_url === attachment.file_url) {
        const nextImage = attachments.find(a => a.file_type === 'image' && a.attachment_id !== attachment.attachment_id);
        setFormData(prev => ({ ...prev, photo_url: nextImage?.file_url || '' }));
      }
      setSnackbar({ open: true, message: 'Attachment removed', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to remove attachment', severity: 'error' });
    }
  };

  const handleSetMainAttachment = async (attachment: AssetAttachment) => {
    if (!editingAsset || attachment.file_type !== 'image') return;
    if (formData.photo_url === attachment.file_url) return; // already main

    setSettingMainAttachmentId(attachment.attachment_id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/assets/${editingAsset.asset_id}/attachments/${attachment.attachment_id}/main`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormData(prev => ({ ...prev, photo_url: attachment.file_url }));
      setSnackbar({ open: true, message: 'Main image updated', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to set main image', severity: 'error' });
    } finally {
      setSettingMainAttachmentId(null);
    }
  };

  const normalizeWholeGoodNumber = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    const upper = trimmed.toUpperCase();
    if (upper.startsWith('WGS')) {
      return 'WGS' + trimmed.slice(3);
    }
    return 'WGS' + trimmed;
  };

  // Custom Fields Management Functions
  const handleOpenCustomFieldsDialog = async (assetType: AssetType) => {
    if (assetType.is_global === 1) {
      setSnackbar({ open: true, message: 'Cannot add custom fields to global asset types', severity: 'error' });
      return;
    }
    setSelectedAssetTypeForCustomFields(assetType);
    setCustomFieldsDialog(true);
    await fetchCustomFields(assetType.asset_type_id);
  };

  const fetchCustomFields = async (assetTypeId: string) => {
    try {
      setLoadingCustomFields(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/custom-fields/asset-types/${assetTypeId}/custom-fields`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success && Array.isArray(response.data.data)) {
        setCustomFields(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      setSnackbar({ open: true, message: 'Failed to fetch custom fields', severity: 'error' });
    } finally {
      setLoadingCustomFields(false);
    }
  };

  const handleOpenCustomFieldFormDialog = (field?: CustomField) => {
    if (field) {
      setEditingCustomField(field);
      setCustomFieldFormData({
        field_name: field.field_name,
        field_label: field.field_label,
        field_type: field.field_type,
        field_options: field.field_options || [],
        is_required: field.is_required,
        default_value: field.default_value || '',
        field_order: field.field_order,
        placeholder: field.placeholder || '',
        help_text: field.help_text || ''
      });
    } else {
      setEditingCustomField(null);
      setCustomFieldFormData({
        field_name: '',
        field_label: '',
        field_type: 'text',
        field_options: [],
        is_required: false,
        default_value: '',
        field_order: customFields.length,
        placeholder: '',
        help_text: ''
      });
    }
    setCustomFieldFormDialog(true);
  };

  const handleCloseCustomFieldFormDialog = () => {
    setCustomFieldFormDialog(false);
    setEditingCustomField(null);
  };

  const handleCustomFieldSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!selectedAssetTypeForCustomFields) return;

      const isUpdating = Boolean(editingCustomField);
      const url = isUpdating
        ? `${API_BASE_URL}/custom-fields/custom-fields/${editingCustomField!.custom_field_id}`
        : `${API_BASE_URL}/custom-fields/asset-types/${selectedAssetTypeForCustomFields.asset_type_id}/custom-fields`;
      const method = isUpdating ? 'PUT' : 'POST';

      await axios.request({
        method,
        url,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: customFieldFormData
      });

      setSnackbar({ 
        open: true, 
        message: isUpdating ? 'Custom field updated' : 'Custom field created', 
        severity: 'success' 
      });
      
      await fetchCustomFields(selectedAssetTypeForCustomFields.asset_type_id);
      handleCloseCustomFieldFormDialog();
    } catch (error: any) {
      console.error('Save custom field failed', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to save custom field', 
        severity: 'error' 
      });
    }
  };

  const handleDeleteCustomField = async (customFieldId: string) => {
    if (!window.confirm('Delete this custom field? All values for this field will be removed from existing assets.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/custom-fields/custom-fields/${customFieldId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: 'Custom field deleted', severity: 'success' });
      if (selectedAssetTypeForCustomFields) {
        await fetchCustomFields(selectedAssetTypeForCustomFields.asset_type_id);
      }
    } catch (error) {
      console.error('Delete custom field failed', error);
      setSnackbar({ open: true, message: 'Failed to delete custom field', severity: 'error' });
    }
  };

  const fetchAssetCustomFields = async (assetId: string, assetTypeId: string) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch custom field definitions and values for this asset
      const response = await axios.get(
        `${API_BASE_URL}/custom-fields/assets/${assetId}/custom-field-values`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.success && Array.isArray(response.data.data)) {
        setCustomFields(response.data.data);
        
        // Populate existing values
        const values: Record<string, string> = {};
        response.data.data.forEach((field: any) => {
          if (field.field_value) {
            values[field.custom_field_id] = field.field_value;
          }
        });
        setCustomFieldValues(values);
      }
    } catch (error) {
      console.error('Error fetching asset custom fields:', error);
    }
  };

  // Import handlers
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/assets/export/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'assets_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSnackbar({ open: true, message: 'Template downloaded successfully', severity: 'success' });
    } catch (error) {
      console.error('Download template failed:', error);
      setSnackbar({ open: true, message: 'Failed to download template', severity: 'error' });
    }
  };

  const handleExportAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/assets/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `assets_export_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSnackbar({ open: true, message: 'Assets exported successfully', severity: 'success' });
    } catch (error) {
      console.error('Export assets failed:', error);
      setSnackbar({ open: true, message: 'Failed to export assets', severity: 'error' });
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportFile(file);
    setImportValidating(true);
    setValidatedData(null);
    setImportResults(null);
    setDuplicateDecisions({});

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/assets/import/validate`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setValidatedData(response.data.data);
      
      if (response.data.data.hasErrors) {
        setSnackbar({ open: true, message: 'Validation completed with errors', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'File validated successfully', severity: 'success' });
      }
    } catch (error: any) {
      console.error('Validation failed:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to validate file', 
        severity: 'error' 
      });
    } finally {
      setImportValidating(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!validatedData || validatedData.hasErrors) return;

    setImportExecuting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/assets/import/execute`, {
        rows: validatedData.rows,
        decisions: duplicateDecisions
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setImportResults(response.data.data);
      setSnackbar({ 
        open: true, 
        message: `Import completed: ${response.data.data.successCount} successful, ${response.data.data.failureCount} failed`, 
        severity: 'success' 
      });
      
      fetchAssets();
      
      setImportFile(null);
      setValidatedData(null);
      setDuplicateDecisions({});
    } catch (error: any) {
      console.error('Import failed:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to execute import', 
        severity: 'error' 
      });
    } finally {
      setImportExecuting(false);
    }
  };

  const handleDuplicateDecision = (rowNumber: number, decision: 'skip' | 'update' | 'create') => {
    setDuplicateDecisions(prev => ({
      ...prev,
      [rowNumber]: decision
    }));
  };

  const handleClearImport = () => {
    setImportFile(null);
    setValidatedData(null);
    setImportResults(null);
    setDuplicateDecisions({});
  };

  // Filter and search assets for the main asset list (using shared filter engine)
  const filteredAssets = useMemo(() => {
    return applyAssetFilters(assets, assetFilters, {
      searchQuery: assetSearchQuery,
      userJobTagIds,
      currentUserId,
    });
  }, [assets, assetSearchQuery, assetFilters, userJobTagIds, currentUserId]);

  const filteredMapData = useMemo(() => {
    return mapData.filter(m => m.latitude !== null && m.longitude !== null);
  }, [mapData]);

  // Filter map data by search query
  const searchFilteredMapData = useMemo(() => {
    if (!mapSearchQuery.trim()) {
      return filteredMapData;
    }
    
    const query = mapSearchQuery.toLowerCase();
    return filteredMapData.filter(m => 
      m.asset_name.toLowerCase().includes(query) ||
      m.asset_type_name?.toLowerCase().includes(query) ||
      m.number_plate?.toLowerCase().includes(query) ||
      m.loan_info?.borrower_name?.toLowerCase().includes(query)
    );
  }, [filteredMapData, mapSearchQuery]);

  // Handle selecting an asset from search
  const handleSelectAssetOnMap = (assetId: string) => {
    const asset = filteredMapData.find(a => a.asset_id === assetId);
    if (asset && asset.latitude && asset.longitude && mapRef.current) {
      setSelectedMapAsset(assetId);
      setShowSearchDropdown(false); // Hide dropdown after selection
      // Fly to the asset location
      mapRef.current.flyTo([asset.latitude, asset.longitude], 15, {
        duration: 1.5
      });
    }
  };

  // MapController component to access map instance
  const MapController = () => {
    const map = useMap();
    React.useEffect(() => {
      mapRef.current = map;
    }, [map]);
    return null;
  };

  // Memoize markers to prevent unnecessary re-renders
  const mapMarkers = useMemo(() => {
    return searchFilteredMapData.map((m) => {
      const isSelected = selectedMapAsset === m.asset_id;
      
      // Create custom icon for selected marker
      const markerIcon = isSelected ? L.icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      }) : L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });

      return (
        <Marker 
          key={m.asset_id} 
          position={[m.latitude as number, m.longitude as number]}
          icon={markerIcon}
          eventHandlers={{
            click: () => setSelectedMapAsset(m.asset_id)
          }}
        >
        <Popup>
          <Box sx={{ minWidth: 260 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <img
                src={m.photo_url ? getImageUrl(m.photo_url) : placeholderImage}
                onError={(e: any) => (e.currentTarget.src = placeholderImage)}
                alt={m.asset_name}
                style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>{m.asset_name}</Typography>
                <Typography variant="caption" color="text.secondary">{m.asset_type_name || 'Asset'}</Typography>
              </Box>
            </Box>
            <Typography variant="body2" gutterBottom>
              {m.number_plate ? `Plate: ${m.number_plate}` : ''}
            </Typography>
            
            {/* Loan Status Badge */}
            {m.location_status === 'on_loan' && m.loan_info && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Chip 
                  label="On Loan" 
                  color="warning" 
                  size="small" 
                  sx={{ fontWeight: 'bold', mb: 1 }}
                />
                {m.loan_info.borrower_name && (
                  <Typography variant="body2" fontWeight={600} color="primary">
                    Loaned to: {m.loan_info.borrower_name}
                  </Typography>
                )}
                {m.loan_info.address && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    📍 {m.loan_info.address}
                  </Typography>
                )}
              </Box>
            )}
            
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {m.location_status === 'on_loan' ? 'Loan address updated: ' : 'Last location update: '}
              {m.location_updated_at ? new Date(m.location_updated_at).toLocaleString() : 'Unknown'}
            </Typography>
            {m.last_check && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Last check: {new Date(m.last_check.completed_at).toLocaleString()} ({m.last_check.check_type})
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" variant="contained" onClick={() => setSelectedAssetForDialog({ asset_id: m.asset_id, asset_name: m.asset_name, last_check_id: m.last_check?.safety_check_id })}>
                View Details
              </Button>
              <Button size="small" variant="outlined" onClick={() => handleShowLocationHistory(m.asset_id, m.asset_name)}>
                Location History
              </Button>
            </Box>
          </Box>
        </Popup>
      </Marker>
    );
    });
  }, [searchFilteredMapData, selectedMapAsset, placeholderImage]);

  const pageHeaderAction =
    (isStandaloneView || activeTab === 'assets') ? (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title={isReorderMode ? 'Exit reorder mode' : 'Drag assets into a custom order'}>
          <Button
            variant={isReorderMode ? 'contained' : 'outlined'}
            color={isReorderMode ? 'success' : 'inherit'}
            startIcon={<ReorderIcon />}
            onClick={() => setIsReorderMode(v => !v)}
            sx={{ fontWeight: 600 }}
          >
            {isReorderMode ? 'Done' : 'Reorder'}
          </Button>
        </Tooltip>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={primaryButtonSx}>
          Add asset
        </Button>
      </Box>
    ) : activeTab === 'types' ? (
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenAssetTypeDialog()} sx={primaryButtonSx}>
        Add type
      </Button>
    ) : null;

  if (isStandaloneView && standaloneViewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isStandaloneView && standaloneViewError) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 1.5 }}>{standaloneViewError}</Alert>
      </Box>
    );
  }

  // Render the component
  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader
        title={isStandaloneView ? (standaloneView?.label ?? 'Asset view') : 'Assets'}
        subtitle={isStandaloneView ? 'Custom view' : 'Operations'}
        action={pageHeaderAction}
      />

      {!isStandaloneView && (
      <Tabs
        value={activeTab}
        onChange={handleAssetsTabChange}
        sx={pageTabsSx}
        variant="fullWidth"
      >
        <Tab label="Assets" value="assets" />
        <Tab label="Asset types" value="types" />
        <Tab label="Import" value="import" />
        <Tab label="Map" value="map" />
        <Tab label="Out of action audit" value="audit" />
        {allowKeyDocuments && <Tab label="Files" value="files" />}
      </Tabs>
      )}

      {!isStandaloneView && activeTab === 'files' && allowKeyDocuments && (
        <Box sx={{ mt: 2 }}>
          <AssetFilesExplorer
            assets={assets}
            loadingAssets={loading}
            onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
          />
        </Box>
      )}

      {(isStandaloneView || activeTab === 'assets') && (
        <>
          <Box sx={{ mb: 2.5 }}>
            <Paper sx={pagePaperSx}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'center' }}>
                {/* Search Bar */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 40%', md: '1 1 25%' }, minWidth: 0 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search assets..."
                    value={assetSearchQuery}
                    onChange={(e) => setAssetSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                      endAdornment: assetSearchQuery && (
                        <IconButton size="small" onClick={() => setAssetSearchQuery('')}>
                          <ClearIcon />
                        </IconButton>
                      )
                    }}
                  />
                </Box>

                {/* Asset Type Filter */}
                <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 15%', md: '1 1 15%' }, minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Asset Type</InputLabel>
                    <Select
                      value={assetFilters.assetType}
                      label="Asset Type"
                      onChange={(e) => setAssetFilters({ ...assetFilters, assetType: e.target.value })}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {assetTypes.map(type => (
                        <MenuItem key={type.asset_type_id} value={type.asset_type_id}>
                          {type.type_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Location Filter */}
                <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 15%', md: '1 1 15%' }, minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Home Location</InputLabel>
                    <Select
                      value={assetFilters.location}
                      label="Home Location"
                      onChange={(e) => setAssetFilters({ ...assetFilters, location: e.target.value })}
                    >
                      <MenuItem value="all">All Locations</MenuItem>
                      {locations.map(loc => (
                        <MenuItem key={loc.locationId} value={loc.locationId}>
                          {loc.locationName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Loan Status Filter */}
                <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 15%', md: '1 1 15%' }, minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Loan Status</InputLabel>
                    <Select
                      value={assetFilters.loanStatus}
                      label="Loan Status"
                      onChange={(e) => setAssetFilters({ ...assetFilters, loanStatus: e.target.value })}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="available">Available</MenuItem>
                      <MenuItem value="loaned_out">Loaned Out</MenuItem>
                      <MenuItem value="overdue">Overdue</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Status Filter */}
                <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 15%', md: '1 1 15%' }, minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={assetFilters.status}
                      label="Status"
                      onChange={(e) => setAssetFilters({ ...assetFilters, status: e.target.value })}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Out of Action Filter */}
                <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 15%', md: '1 1 15%' }, minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Out of Action</InputLabel>
                    <Select
                      value={assetFilters.outOfAction}
                      label="Out of Action"
                      onChange={(e) => setAssetFilters({ ...assetFilters, outOfAction: e.target.value })}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="true">Out of Action</MenuItem>
                      <MenuItem value="false">In Action</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Lock Status Filter */}
                <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 15%', md: '1 1 15%' }, minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Check Lock</InputLabel>
                    <Select
                      value={assetFilters.lockStatus}
                      label="Check Lock"
                      onChange={(e) => setAssetFilters({ ...assetFilters, lockStatus: e.target.value })}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="locked">In Use (Locked)</MenuItem>
                      <MenuItem value="unlocked">Available</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Tag Filter */}
                {(jobTags.length > 0 || userJobTagIds.length > 0) && (
                  <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 15%', md: '1 1 15%' }, minWidth: 0 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tag</InputLabel>
                      <Select
                        value={assetFilters.jobTag}
                        label="Tag"
                        onChange={(e) => {
                          const v = e.target.value;
                          try {
                            sessionStorage.setItem(getAssetsJobTagFilterKey(), v);
                          } catch {
                            /* ignore */
                          }
                          setAssetFilters({ ...assetFilters, jobTag: v });
                        }}
                      >
                        <MenuItem value="all">All Tags</MenuItem>
                        {userJobTagIds.length > 0 && (
                          <MenuItem value="my_tags">My job role(s)</MenuItem>
                        )}
                        {jobTags.filter(t => t.tag_scope !== 'asset').map((tag) => (
                          <MenuItem key={tag.job_tag_id} value={tag.job_tag_id}>
                            {tag.tag_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>

              {/* Active Filters Display */}
              {!isFiltersDefault(assetFilters, assetSearchQuery) && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Active filters:
                  </Typography>
                  {assetSearchQuery && (
                    <Chip
                      label={`Search: "${assetSearchQuery}"`}
                      size="small"
                      onDelete={() => setAssetSearchQuery('')}
                    />
                  )}
                  {assetFilters.assetType !== 'all' && (
                    <Chip
                      label={`Type: ${assetTypes.find(t => t.asset_type_id === assetFilters.assetType)?.type_name}`}
                      size="small"
                      onDelete={() => setAssetFilters({ ...assetFilters, assetType: 'all' })}
                    />
                  )}
                  {assetFilters.location !== 'all' && (
                    <Chip
                      label={`Home Location: ${locations.find(l => l.locationId === assetFilters.location)?.locationName}`}
                      size="small"
                      onDelete={() => setAssetFilters({ ...assetFilters, location: 'all' })}
                    />
                  )}
                  {assetFilters.loanStatus !== 'all' && (
                    <Chip
                      label={`Loan: ${assetFilters.loanStatus.replace('_', ' ')}`}
                      size="small"
                      onDelete={() => setAssetFilters({ ...assetFilters, loanStatus: 'all' })}
                    />
                  )}
                  {assetFilters.status !== 'all' && (
                    <Chip
                      label={`Status: ${assetFilters.status}`}
                      size="small"
                      onDelete={() => setAssetFilters({ ...assetFilters, status: 'all' })}
                    />
                  )}
                  {assetFilters.outOfAction !== 'all' && (
                    <Chip
                      label={`Out of Action: ${assetFilters.outOfAction === 'true' ? 'Yes' : 'No'}`}
                      size="small"
                      onDelete={() => setAssetFilters({ ...assetFilters, outOfAction: 'all' })}
                    />
                  )}
                  {assetFilters.lockStatus !== 'all' && (
                    <Chip
                      label={`Check Lock: ${assetFilters.lockStatus === 'locked' ? 'In Use' : 'Available'}`}
                      size="small"
                      onDelete={() => setAssetFilters({ ...assetFilters, lockStatus: 'all' })}
                      sx={{ bgcolor: '#f3e5f5', color: '#4A148C' }}
                    />
                  )}
                  {assetFilters.jobTag !== 'all' && (
                    <Chip
                      label={
                        assetFilters.jobTag === 'my_tags'
                          ? 'Tag: My job role(s)'
                          : `Tag: ${jobTags.find((t) => t.job_tag_id === assetFilters.jobTag)?.tag_name || 'Tag'}`
                      }
                      size="small"
                      onDelete={() => {
                        try {
                          sessionStorage.setItem(getAssetsJobTagFilterKey(), 'all');
                        } catch {
                          /* ignore */
                        }
                        setAssetFilters({ ...assetFilters, jobTag: 'all' });
                      }}
                      sx={{ bgcolor: '#e8f5e9', color: '#1b5e20' }}
                    />
                  )}
                  {(assetFilters.excludeJobTagIds ?? []).map(tagId => {
                    const tag = jobTags.find(t => t.job_tag_id === tagId);
                    return tag ? (
                      <Chip
                        key={tagId}
                        label={`Exclude: ${tag.tag_name}`}
                        size="small"
                        onDelete={() => setAssetFilters({
                          ...assetFilters,
                          excludeJobTagIds: (assetFilters.excludeJobTagIds ?? []).filter(id => id !== tagId),
                        })}
                        sx={{ bgcolor: '#fce4ec', color: '#880e4f' }}
                      />
                    ) : null;
                  })}
                  <Button
                    size="small"
                    onClick={() => {
                      try {
                        sessionStorage.setItem(getAssetsJobTagFilterKey(), 'all');
                      } catch {
                        /* ignore */
                      }
                      setAssetSearchQuery('');
                      setAssetFilters({ ...DEFAULT_ASSET_FILTERS });
                    }}
                  >
                    Clear All
                  </Button>
                </Box>
              )}

              {/* Results Count */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredAssets.length} of {assets.length} assets
                </Typography>
              </Box>
            </Paper>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 1.5 }}>{error}</Alert>
          ) : filteredAssets.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 1.5 }}>
              No assets found matching your search criteria. Try adjusting your filters.
            </Alert>
          ) : (
            <Box>
              {isReorderMode && (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="assets-reorder">
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                      >
                        {assets.map((asset, index) => (
                          <Draggable key={asset.asset_id} draggableId={asset.asset_id} index={index}>
                            {(provided, snapshot) => (
                              <Paper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                                  boxShadow: snapshot.isDragging ? 4 : 0,
                                  cursor: 'grab',
                                  userSelect: 'none',
                                }}
                              >
                                <Box {...provided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', cursor: 'grab' }}>
                                  <DragIndicatorIcon />
                                </Box>
                                {asset.photo_url ? (
                                  <Box
                                    component="img"
                                    src={getImageUrl(asset.photo_url)}
                                    onError={handleImageError}
                                    alt={asset.asset_name}
                                    sx={{ width: 44, height: 44, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }}
                                  />
                                ) : (
                                  <Box sx={{ width: 44, height: 44, borderRadius: 1.5, background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <ImageIcon sx={{ fontSize: 22, color: 'grey.400' }} />
                                  </Box>
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={600} noWrap>{asset.asset_name}</Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {asset.asset_type_name} · {asset.location_name}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, minWidth: 28, textAlign: 'right' }}>
                                  #{index + 1}
                                </Typography>
                              </Paper>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {isSavingOrder && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                            <CircularProgress size={18} />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
              {!isReorderMode && (() => {
                const isDefaultView = isFiltersDefault(assetFilters, assetSearchQuery);

                const myTagSet = new Set(userJobTagIds.map(String));
                const isMyRelevantAsset = (asset: Asset) =>
                  (!!currentUserId && !!asset.primary_user_id && String(asset.primary_user_id) === String(currentUserId)) ||
                  (myTagSet.size > 0 && (asset.job_tags || []).some(t => myTagSet.has(String(t.job_tag_id))));

                const showSections = isDefaultView && !!currentUserId &&
                  filteredAssets.some(isMyRelevantAsset);

                let otherSectionStarted = false;

                return (
                  <Box sx={assetGridSx}>
                    {showSections && (
                      <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PersonIcon color="primary" sx={{ fontSize: '1.15rem' }} />
                        <Typography sx={{ ...sectionTitleSx, color: 'primary.main' }}>My assets</Typography>
                        <Chip
                          label={filteredAssets.filter(isMyRelevantAsset).length}
                          size="small"
                          color="primary"
                          sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
                        />
                      </Box>
                    )}
                    {filteredAssets.map(asset => {
                      const isRelevant = isMyRelevantAsset(asset);
                      let divider = null;
                      if (showSections && !isRelevant && !otherSectionStarted) {
                        otherSectionStarted = true;
                        divider = (
                          <Box key="divider-all" sx={{ gridColumn: '1 / -1' }}>
                            <Divider sx={{ my: 0.5 }}>
                              <Typography sx={{ ...sectionLabelSx, px: 1 }}>All other assets</Typography>
                            </Divider>
                          </Box>
                        );
                      }
                      return (
                        <React.Fragment key={asset.asset_id}>
                          {divider}
                          <Card
                      sx={{
                        ...pageCardSx,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        transition: 'border-color 0.15s ease',
                        '&:hover': {
                          borderColor: 'rgba(0,0,0,0.28)',
                        },
                      }}
                    >
                      {/* Image area with gradient overlay */}
                      <Box sx={{ position: 'relative', pt: '56.25%', flexShrink: 0 }}>
                        {asset.photo_url ? (
                          <CardMedia
                            component="img"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            image={getImageUrl(asset.photo_url)}
                            alt={asset.asset_name}
                            onError={handleImageError}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 52, color: 'grey.300' }} />
                          </Box>
                        )}
                        {/* Gradient scrim so chips are readable over any image */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '60%',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
                            pointerEvents: 'none',
                          }}
                        />
                        {/* Status chips at bottom of image */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                            right: 8,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            alignItems: 'flex-end',
                          }}
                        >
                          <Chip
                            label={asset.asset_type_name}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(255,255,255,0.88)',
                              color: 'text.primary',
                              fontWeight: 700,
                              fontSize: '0.68rem',
                              height: 20,
                              backdropFilter: 'blur(4px)',
                            }}
                          />
                          {asset.is_overdue && (
                            <Chip
                              label="Overdue"
                              size="small"
                              color="error"
                              sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                            />
                          )}
                          {asset.is_out_of_action && (
                            <Chip
                              label="Out of Action"
                              size="small"
                              color="error"
                              sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                            />
                          )}
                          {asset.is_loaned_out && !asset.is_overdue && (
                            <Chip
                              label="Loaned Out"
                              size="small"
                              color="warning"
                              sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                            />
                          )}
                          {asset.locked_by_user_id && (
                            <Chip
                              label="In Use"
                              size="small"
                              sx={{
                                backgroundColor: '#7B1FA2',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.68rem',
                                height: 20,
                                cursor: 'pointer',
                              }}
                              onClick={(e) => { e.stopPropagation(); handleOpenUnlockDialog(asset); }}
                            />
                          )}
                        </Box>
                      </Box>

                      <CardContent sx={{ flexGrow: 1, py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                        <Typography
                          sx={{ ...sectionTitleSx, fontSize: '0.95rem', mb: 0.75 }}
                          noWrap
                          title={asset.asset_name}
                        >
                          {asset.asset_name}
                        </Typography>

                        {/* Home Location / Serial / Plate rows */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: '0.85rem', color: 'text.disabled', flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary" noWrap>
                              Home Location: {asset.location_name}
                            </Typography>
                          </Box>
                          {asset.serial_number && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FingerprintIcon sx={{ fontSize: '0.85rem', color: 'text.disabled', flexShrink: 0 }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {asset.serial_number}
                              </Typography>
                            </Box>
                          )}
                          {asset.number_plate && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DirectionsCarIcon sx={{ fontSize: '0.85rem', color: 'text.disabled', flexShrink: 0 }} />
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                                {asset.number_plate}
                              </Typography>
                              <Tooltip title="View MOT History">
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.25 }}
                                  onClick={(e) => { e.stopPropagation(); openMotHistory(asset); }}
                                >
                                  <HistoryIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {/* MOT / Tax chips */}
                        {(asset.mot_status || asset.tax_status) && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {asset.mot_status && (() => {
                              const cat = getMotStatusCategory(asset.mot_due_date, asset.mot_status);
                              const color = cat === 'expired' ? 'error' : cat === 'due_soon' ? 'warning' : 'success';
                              const label = cat === 'expired' ? 'MOT Expired' : cat === 'due_soon' ? 'MOT Due Soon' : 'MOT Valid';
                              return <Chip label={label} size="small" color={color as any} sx={{ fontSize: '0.68rem', height: 20 }} />;
                            })()}
                            {asset.tax_status && (() => {
                              const cat = getTaxStatusCategory(asset.tax_due_date, asset.tax_status);
                              const color = cat === 'expired' ? 'error' : cat === 'due_soon' ? 'warning' : 'success';
                              const label = cat === 'expired' ? 'Untaxed' : cat === 'due_soon' ? 'Tax Due Soon' : 'Taxed';
                              return <Chip label={label} size="small" color={color as any} sx={{ fontSize: '0.68rem', height: 20 }} />;
                            })()}
                          </Box>
                        )}

                        {/* Manufacturer / Model */}
                        {asset.manufacturer && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 0.75 }}>
                            {asset.manufacturer}{asset.model ? ` · ${asset.model}` : ''}
                          </Typography>
                        )}

                        {/* Last checked */}
                        {asset.last_check_date && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                            <CheckCircleIcon sx={{ fontSize: '0.85rem', color: 'success.main', flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary">
                              Checked {new Date(asset.last_check_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}

                        {/* Tags */}
                        {asset.job_tags && asset.job_tags.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                            {asset.job_tags.map(tag => (
                              <Chip
                                key={tag.job_tag_id}
                                label={tag.tag_name}
                                size="small"
                                sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.68rem', height: 20 }}
                              />
                            ))}
                          </Box>
                        )}

                        {/* Primary User */}
                        {asset.primary_user_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                            <PersonIcon sx={{ fontSize: '0.85rem', color: 'text.disabled', flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {asset.primary_user_name}
                            </Typography>
                          </Box>
                        )}

                        {/* Out of Action banner */}
                        {asset.is_out_of_action && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.25,
                              bgcolor: '#fff5f5',
                              border: '1px solid',
                              borderColor: 'error.light',
                              borderRadius: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.dark', display: 'block', mb: 0.25 }}>
                              Out of Action
                            </Typography>
                            {asset.out_of_action_note && (
                              <>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {asset.out_of_action_note}
                                </Typography>
                                {asset.out_of_action_note.length > 100 && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setNoteDialog({
                                      open: true,
                                      title: `Out of Action Note: ${asset.asset_name}`,
                                      note: asset.out_of_action_note || '',
                                      assetName: asset.asset_name,
                                    })}
                                    sx={{
                                      textTransform: 'none',
                                      fontSize: '0.7rem',
                                      p: 0,
                                      minWidth: 'auto',
                                      height: 'auto',
                                      color: 'error.main',
                                      fontWeight: 600,
                                      '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' },
                                    }}
                                  >
                                    Read more
                                  </Button>
                                )}
                              </>
                            )}
                            {asset.out_of_action_date && (
                              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                                Since {new Date(asset.out_of_action_date).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* In Use / Check In Progress banner */}
                        {asset.locked_by_user_id && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.25,
                              bgcolor: '#f3e5f5',
                              border: '1px solid #ce93d8',
                              borderRadius: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#4A148C', display: 'block', mb: 0.25 }}>
                              Check In Progress
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              By: {asset.locked_by_name}
                            </Typography>
                            {asset.locked_at && (
                              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                                Since {new Date(asset.locked_at).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </CardContent>

                      <CardActions sx={{ px: 2, pb: 2, pt: 0.5, flexDirection: 'column', gap: 0.75 }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<AssessmentIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            logger.debug('View All Checks clicked for asset:', asset.asset_name, asset.asset_id);
                            setSelectedAssetForChecks({ asset_id: asset.asset_id, asset_name: asset.asset_name });
                          }}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          View All Checks
                        </Button>
                        {asset.number_plate && allowMotTaxTab && (
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={motCheckingIds.has(asset.asset_id) ? <CircularProgress size={13} color="inherit" /> : <DirectionsCarIcon />}
                            disabled={motCheckingIds.has(asset.asset_id)}
                            onClick={(e) => { e.stopPropagation(); runMotCheckForAsset(asset); }}
                            sx={{ textTransform: 'none' }}
                          >
                            {motCheckingIds.has(asset.asset_id)
                              ? 'Checking…'
                              : asset.mot_status
                                ? 'Refresh MOT / Tax'
                                : 'Run MOT Check'}
                          </Button>
                        )}
                        {asset.locked_by_user_id && (
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: '#7B1FA2', color: '#7B1FA2', textTransform: 'none', '&:hover': { borderColor: '#4A148C', color: '#4A148C', bgcolor: 'transparent' } }}
                            onClick={(e) => { e.stopPropagation(); handleOpenUnlockDialog(asset); }}
                          >
                            View &amp; Unlock Check
                          </Button>
                        )}
                        {/* Icon row: Edit / Out-of-Action / Delete / QR */}
                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                          <Tooltip title="Edit asset">
                            <IconButton size="small" onClick={() => handleOpenDialog(asset)} sx={{ color: 'text.secondary' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={asset.is_out_of_action ? 'Mark as Available' : 'Mark Out of Action'}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOutOfActionDialog({ open: true, asset: asset, note: '', isOutOfAction: Boolean(asset.is_out_of_action), images: [], uploadingImages: false });
                              }}
                              sx={{ color: asset.is_out_of_action ? 'success.main' : 'error.main' }}
                            >
                              <WarningIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={asset.qr_code || asset.serial_number ? 'View QR Code' : 'No QR code or serial number set'}>
                            <span>
                              <IconButton size="small" onClick={() => handleOpenQrDialog(asset)} disabled={!asset.qr_code && !asset.serial_number} sx={{ color: 'text.secondary' }}>
                                <QrCodeIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete asset">
                            <IconButton size="small" onClick={() => handleDelete(asset.asset_id)} sx={{ color: 'error.main' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardActions>
                    </Card>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                );
              })()}
              
              {assets.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                  <ImageIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">No assets found</Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>Add your first asset to get started</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                  >
                    Add Asset
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {!isStandaloneView && activeTab === 'types' && (
        <Paper sx={{ ...pagePaperSx, p: 0, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Vehicle Registration</TableCell>
                  <TableCell>Wholegood Number</TableCell>
                  <TableCell>Hours / Mileage</TableCell>
                  <TableCell>Attachments</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assetTypes.map((assetType) => (
                  <TableRow key={assetType.asset_type_id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {assetType.type_name}
                        {assetType.is_global === 1 && (
                          <Chip 
                            label="Global" 
                            size="small" 
                            color="primary"
                            variant="filled"
                            sx={{ 
                              backgroundColor: '#1976d2',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{assetType.description}</TableCell>
                    <TableCell>
                      {assetType.requires_car_registration ? (
                        <Chip 
                          label="Required" 
                          size="small" 
                          color="warning"
                          variant="filled"
                          icon={<DirectionsCarIcon />}
                        />
                      ) : (
                        <Chip 
                          label="Not Required" 
                          size="small" 
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                  <TableCell>
                    {assetType.has_whole_good_number ? (
                      <Chip
                        label="Enabled"
                        size="small"
                        color="success"
                        variant="filled"
                      />
                    ) : (
                      <Chip
                        label="Disabled"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {assetType.has_recorded_hours ? (
                      <Chip
                        label="Enabled"
                        size="small"
                        color="success"
                        variant="filled"
                      />
                    ) : (
                      <Chip
                        label="Disabled"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {assetType.allow_loan_attachments ? (
                      <Chip
                        label="Allowed"
                        size="small"
                        color="success"
                        variant="filled"
                      />
                    ) : (
                      <Chip
                        label="Not allowed"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                    <TableCell>
                      {assetType.is_global === 1 ? (
                        <Chip 
                          label="Platform Wide" 
                          size="small" 
                          color="info"
                          variant="outlined"
                        />
                      ) : (
                        <Chip 
                          label="Business Specific" 
                          size="small" 
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenCustomFieldsDialog(assetType)}
                        disabled={assetType.is_global === 1}
                        sx={{ mr: 1 }}
                      >
                        Manage Fields
                      </Button>
                      <IconButton 
                        onClick={() => handleOpenAssetTypeDialog(assetType)}
                        disabled={assetType.is_global === 1}
                        sx={{
                          opacity: assetType.is_global === 1 ? 0.5 : 1,
                          cursor: assetType.is_global === 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDeleteAssetType(assetType.asset_type_id, assetType)}
                        disabled={assetType.is_global === 1}
                        sx={{
                          opacity: assetType.is_global === 1 ? 0.5 : 1,
                          cursor: assetType.is_global === 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {!isStandaloneView && activeTab === 'import' && (
        <Box>
          <Typography sx={{ ...sectionLabelSx, mb: 0.5 }}>Data</Typography>
          <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Import assets</Typography>

          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={handleExportAssets}
            >
              Export Existing Assets
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Download the template to get your business-specific import file with reference sheets for Asset Types, Locations, Users, Asset Tags, Custom Fields, and a column guide.
            Required columns: Asset Name, Serial Number, Asset Type, and Home Location.
            Optional columns include manufacturer details, QR codes, Recorded Hours, Wholegood Number, Primary User Email, Status, Out of Action, Enabled for Offline, Asset Tags, and any custom fields configured on your asset types.
          </Alert>

          {!importResults && (
            <Paper sx={{ ...pagePaperSx, mb: 2.5 }}>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="import-file-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="import-file-upload">
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {importFile ? importFile.name : 'Click to upload Excel file'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supports .xlsx and .xls files
                  </Typography>
                </Box>
              </label>
            </Paper>
          )}

          {importValidating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {validatedData && !importResults && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Validation Results</Typography>
                <Button onClick={handleClearImport}>Clear</Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip label={`Total: ${validatedData.totalRows}`} />
                <Chip label={`Valid: ${validatedData.validRows}`} color="success" />
                {validatedData.rowsWithErrors > 0 && (
                  <Chip label={`Errors: ${validatedData.rowsWithErrors}`} color="error" />
                )}
                {validatedData.duplicatesFound > 0 && (
                  <Chip label={`Duplicates: ${validatedData.duplicatesFound}`} color="warning" />
                )}
              </Box>

              {validatedData.hasErrors && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Please fix the errors before importing
                </Alert>
              )}

              {(validatedData.missingAssetTypes?.length > 0 || validatedData.missingLocations?.length > 0) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <strong>Missing Required Items:</strong>
                  {validatedData.missingAssetTypes?.length > 0 && (
                    <div>
                      <strong>Asset Types:</strong> {validatedData.missingAssetTypes.join(', ')}
                      <br />Please create these in Asset Types management before importing.
                    </div>
                  )}
                  {validatedData.missingLocations?.length > 0 && (
                    <div style={{ marginTop: validatedData.missingAssetTypes?.length > 0 ? '8px' : '0' }}>
                      <strong>Locations:</strong> {validatedData.missingLocations.join(', ')}
                      <br />Please create these in Location management before importing.
                    </div>
                  )}
                </Alert>
              )}

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Asset Name</TableCell>
                      <TableCell>Serial Number</TableCell>
                      <TableCell>Asset Type</TableCell>
                      <TableCell>Home Location</TableCell>
                      <TableCell>Manufacturer</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>Purchase Date</TableCell>
                      <TableCell>Number Plate</TableCell>
                      <TableCell>QR Code</TableCell>
                      <TableCell>3rd Party QR</TableCell>
                      <TableCell>Hours / Mileage</TableCell>
                      <TableCell>Wholegood Number</TableCell>
                      <TableCell>Primary User</TableCell>
                      <TableCell>Asset Status</TableCell>
                      <TableCell>Out of Action</TableCell>
                      <TableCell>Tags</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Validation</TableCell>
                      {validatedData.duplicatesFound > 0 && <TableCell>Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validatedData.rows.map((row: any) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.data.asset_name}</TableCell>
                        <TableCell>{row.data.serial_number}</TableCell>
                        <TableCell>{row.data.asset_type}</TableCell>
                        <TableCell>{row.data.location}</TableCell>
                        <TableCell>{row.data.manufacturer || '-'}</TableCell>
                        <TableCell>{row.data.model || '-'}</TableCell>
                        <TableCell>{formatImportDate(row.data.purchase_date)}</TableCell>
                        <TableCell>{row.data.number_plate || '-'}</TableCell>
                        <TableCell>{row.data.qr_code || '-'}</TableCell>
                        <TableCell>{row.data.third_party_qr || '-'}</TableCell>
                        <TableCell>{row.data.recorded_hours !== null ? row.data.recorded_hours : '-'}</TableCell>
                        <TableCell>
                          {row.data.whole_good_number ? (
                            <Chip 
                              label={row.data.whole_good_number} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell>{row.data.primary_user_email || '-'}</TableCell>
                        <TableCell>{row.data.status || 'active'}</TableCell>
                        <TableCell>{row.data.is_out_of_action ? 'Yes' : 'No'}</TableCell>
                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.data.asset_tags || '-'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.data.description || '-'}
                        </TableCell>
                        <TableCell>
                          {row.errors.length > 0 ? (
                            <Tooltip title={
                              <Box>
                                <strong>Errors:</strong>
                                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                  {row.errors.map((error: string, idx: number) => (
                                    <li key={idx}>{error}</li>
                                  ))}
                                </ul>
                              </Box>
                            }>
                              <Chip label={`Error (${row.errors.length})`} color="error" size="small" />
                            </Tooltip>
                          ) : row.warnings?.length > 0 ? (
                            <Tooltip title={
                              <Box>
                                <strong>Warnings:</strong>
                                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                  {row.warnings.map((warning: string, idx: number) => (
                                    <li key={idx}>{warning}</li>
                                  ))}
                                </ul>
                              </Box>
                            }>
                              <Chip label={row.isDuplicate ? "Duplicate" : `Warning (${row.warnings.length})`} color="warning" size="small" />
                            </Tooltip>
                          ) : row.isDuplicate ? (
                            <Chip label="Duplicate" color="warning" size="small" />
                          ) : (
                            <Chip label="Valid" color="success" size="small" />
                          )}
                        </TableCell>
                        {validatedData.duplicatesFound > 0 && (
                          <TableCell>
                            {row.isDuplicate && (
                              <ButtonGroup size="small">
                                <Button
                                  variant={duplicateDecisions[row.rowNumber] === 'skip' ? 'contained' : 'outlined'}
                                  onClick={() => handleDuplicateDecision(row.rowNumber, 'skip')}
                                >
                                  Skip
                                </Button>
                                <Button
                                  variant={duplicateDecisions[row.rowNumber] === 'update' ? 'contained' : 'outlined'}
                                  onClick={() => handleDuplicateDecision(row.rowNumber, 'update')}
                                >
                                  Update
                                </Button>
                              </ButtonGroup>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {!validatedData.hasErrors && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleExecuteImport}
                    disabled={importExecuting}
                  >
                    {importExecuting ? <CircularProgress size={24} /> : 'Execute Import'}
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          {importResults && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Import Complete
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip label={`Total: ${importResults.totalRows}`} />
                <Chip label={`Successful: ${importResults.successCount}`} color="success" />
                <Chip label={`Failed: ${importResults.failureCount}`} color="error" />
                <Chip label={`Skipped: ${importResults.skippedCount}`} color="default" />
              </Box>
              <Button variant="contained" onClick={handleClearImport}>
                Import More Assets
              </Button>
            </Paper>
          )}
        </Box>
      )}

      {!isStandaloneView && activeTab === 'map' && (
        <>
        <Paper
          sx={{
            ...pagePaperSx,
            p: 0,
            overflow: 'hidden',
          }}
        >
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <Box>
                <Typography sx={sectionLabelSx}>Locations</Typography>
                <Typography sx={sectionTitleSx}>Asset map</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant={showLocationHistory ? 'contained' : 'outlined'}
                  onClick={toggleLocationHistory}
                  disabled={loadingMap}
                  sx={primaryButtonSx}
                >
                  {showLocationHistory ? 'Hide history' : 'Show history'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={fetchLatestAssetLocations}
                  disabled={loadingMap}
                  sx={primaryButtonSx}
                >
                  {loadingMap ? 'Refreshing…' : 'Refresh'}
                </Button>
              </Box>
            </Box>
            <Box sx={{ height: { xs: 420, sm: 520, lg: 600 }, position: 'relative' }}>
              {/* Map Search Box */}
              <Box sx={{ 
                position: 'absolute', 
                top: 10, 
                left: '50%', 
                transform: 'translateX(-50%)', 
                zIndex: 1000,
                width: isMobile ? '90%' : '400px'
              }}>
                <Paper elevation={4} sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
                  <SearchIcon color="action" />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search assets by name, type, plate, or borrower..."
                    variant="standard"
                    value={mapSearchQuery}
                    onChange={(e) => {
                      setMapSearchQuery(e.target.value);
                      setShowSearchDropdown(true); // Show dropdown when typing
                    }}
                    onFocus={() => {
                      if (mapSearchQuery && searchFilteredMapData.length > 0) {
                        setShowSearchDropdown(true);
                      }
                    }}
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: mapSearchQuery && (
                        <IconButton size="small" onClick={() => { 
                          setMapSearchQuery(''); 
                          setSelectedMapAsset(null);
                          setShowSearchDropdown(false); // Hide dropdown when cleared
                        }}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )
                    }}
                  />
                  {searchFilteredMapData.length > 0 && mapSearchQuery && (
                    <Chip 
                      label={`${searchFilteredMapData.length} found`} 
                      size="small" 
                      color="primary" 
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Paper>
                
                {/* Search Results Dropdown */}
                {showSearchDropdown && mapSearchQuery && searchFilteredMapData.length > 0 && searchFilteredMapData.length <= 10 && (
                  <Paper elevation={6} sx={{ mt: 0.5, maxHeight: 300, overflow: 'auto', backgroundColor: 'rgba(255, 255, 255, 0.98)' }}>
                    {searchFilteredMapData.map((asset) => (
                      <Box
                        key={asset.asset_id}
                        onClick={() => handleSelectAssetOnMap(asset.asset_id)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          },
                          backgroundColor: selectedMapAsset === asset.asset_id ? '#e3f2fd' : 'transparent'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MyLocationIcon fontSize="small" color={selectedMapAsset === asset.asset_id ? 'primary' : 'action'} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={selectedMapAsset === asset.asset_id ? 700 : 600}>
                              {asset.asset_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {asset.asset_type_name}
                              {asset.number_plate && ` • ${asset.number_plate}`}
                              {asset.location_status === 'on_loan' && asset.loan_info?.borrower_name && ` • Loaned to: ${asset.loan_info.borrower_name}`}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                )}
                
                {showSearchDropdown && mapSearchQuery && searchFilteredMapData.length === 0 && (
                  <Paper elevation={6} sx={{ mt: 0.5, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.98)' }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No assets found matching "{mapSearchQuery}"
                    </Typography>
                  </Paper>
                )}
              </Box>
              
              {loadingMap && (
                <Box sx={{ 
                  position: 'absolute', 
                  inset: 0, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  zIndex: 1000,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(4px)'
                }}>
                  <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                    Loading Asset Locations
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {mapData.length > 0 ? `Rendering ${mapData.length} assets...` : 'Fetching data...'}
                  </Typography>
                </Box>
              )}
              <MapContainer style={{ height: '100%', width: '100%', filter: 'saturate(1.2) contrast(1.05)' }} center={[51.5074, -0.1278]} zoom={6}>
                <MapController />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
                {mapMarkers}
                  
                  {/* Historical Location Markers */}
                  {showLocationHistory && historicalLocations.flatMap((assetHistory) => 
                    assetHistory.locations.slice(1).map((location, index) => {
                      // Use orange color for loan addresses, yellow for others
                      const markerColor = location.source === 'loan_address' 
                        ? 'rgba(255, 152, 0, 0.8)' // Orange for loan addresses
                        : 'rgba(255, 193, 7, 0.8)'; // Yellow for other history
                      const borderColor = location.source === 'loan_address'
                        ? 'rgba(255, 152, 0, 1)'
                        : 'rgba(255, 193, 7, 1)';
                      
                      return (
                        <Marker 
                          key={`${assetHistory.asset_id}-history-${location.id}`} 
                          position={[location.latitude, location.longitude]}
                          icon={L.divIcon({
                            className: 'custom-div-icon',
                            html: `<div style="background-color: ${markerColor}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid ${borderColor};"></div>`,
                            iconSize: [12, 12],
                            iconAnchor: [6, 6]
                          })}
                        >
                          <Popup>
                            <Box sx={{ minWidth: 200 }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {assetHistory.asset_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {location.source_description}
                              </Typography>
                              
                              {/* Loan Address Information */}
                              {location.source === 'loan_address' && (
                                <Box sx={{ mt: 1, mb: 1, p: 1, backgroundColor: '#fff3cd', borderRadius: 1 }}>
                                  {location.borrower_name && (
                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                      📍 Loaned to: {location.borrower_name}
                                    </Typography>
                                  )}
                                  {location.loan_address && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {location.loan_address}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Coordinates:</strong> {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                <strong>Recorded:</strong> {new Date(location.created_at).toLocaleString()}
                              </Typography>
                            </Box>
                          </Popup>
                        </Marker>
                      );
                    })
                  )}
              </MapContainer>
            </Box>
          </Paper>

          <Dialog open={!!selectedAssetForDialog} onClose={() => setSelectedAssetForDialog(null)} maxWidth="lg" fullWidth>
            <DialogTitle>
              {selectedAssetForDialog?.asset_name} — Recent Checks & Images
            </DialogTitle>
            <DialogContent dividers>
              {selectedAssetForDialog && (
                <CheckResponses extraQuery={{ asset_id: selectedAssetForDialog.asset_id }} />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedAssetForDialog(null)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Location History Dialog */}
          <Dialog open={!!locationHistoryDialog} onClose={handleCloseLocationHistoryDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              {locationHistoryDialog?.asset_name} — Location History
            </DialogTitle>
            <DialogContent dividers>
              {loadingLocationHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : locationHistory.length > 0 ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Showing {locationHistory.length} location records for this asset
                  </Typography>
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {locationHistory.map((location, index) => (
                      <Card key={location.id} sx={{ mb: 2, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {location.source_description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {location.location_name}
                            </Typography>
                          </Box>
                          <Chip 
                            label={location.source === 'asset_location' ? 'Home Location' : location.source === 'loan_address' ? 'Loan' : 'Check'} 
                            size="small" 
                            color={location.source === 'asset_location' ? 'primary' : location.source === 'loan_address' ? 'warning' : 'secondary'}
                            variant="outlined"
                          />
                        </Box>
                        
                        {/* Loan Address Information */}
                        {location.source === 'loan_address' && (
                          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                            {location.borrower_name && (
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                📍 Loaned to: {location.borrower_name}
                              </Typography>
                            )}
                            {location.loan_address && (
                              <Typography variant="body2" color="text.secondary">
                                {location.loan_address}
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                          <Typography variant="body2">
                            <strong>Coordinates:</strong> {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Recorded:</strong> {new Date(location.created_at).toLocaleString()}
                          </Typography>
                          {location.check_type && (
                            <Chip 
                              label={location.check_type} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {location.is_home_location && (
                          <Chip 
                            label="Home Location" 
                            size="small" 
                            color="success" 
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Card>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No location history found for this asset.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Location history will appear here when safety checks are completed or locations are manually updated.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseLocationHistoryDialog}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}


      {!isStandaloneView && activeTab === 'audit' && (
          <Paper sx={pagePaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Box>
                <Typography sx={sectionLabelSx}>Audit</Typography>
                <Typography sx={sectionTitleSx}>Out of action log</Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchAuditLogs}
                disabled={loadingAuditLogs}
                startIcon={loadingAuditLogs ? <CircularProgress size={18} /> : <AssessmentIcon />}
                sx={primaryButtonSx}
              >
                Refresh
              </Button>
            </Box>

            {loadingAuditLogs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : auditLogs.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No audit logs found
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date & Time</strong></TableCell>
                      <TableCell><strong>Asset</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                      <TableCell><strong>Note</strong></TableCell>
                      <TableCell><strong>User</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLogs.map((log) => (
                        <TableRow key={log.audit_id} hover>
                          <TableCell>
                            {new Date(log.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {log.asset_name || 'Unknown Asset'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.action === 'marked_out_of_action' ? 'Marked Out of Action' : 'Marked Available'}
                              color={log.action === 'marked_out_of_action' ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {log.note ? (
                              <Box>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    maxWidth: 300,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    mb: 0.5
                                  }}
                                >
                                  {log.note}
                                </Typography>
                                {log.note.length > 100 && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setNoteDialog({
                                      open: true,
                                      title: `Note for ${log.asset_name || 'Asset'}`,
                                      note: log.note || '',
                                      assetName: log.asset_name || undefined,
                                    })}
                                    sx={{ 
                                      textTransform: 'none',
                                      fontSize: '0.75rem',
                                      p: 0,
                                      minWidth: 'auto',
                                      height: 'auto'
                                    }}
                                  >
                                    Read more...
                                  </Button>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                No note
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {log.first_name && log.last_name
                                ? `${log.first_name} ${log.last_name}`
                                : log.email || 'Unknown User'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
      )}

      {/* Asset Checks Dialog - Available from all tabs */}
      <Dialog 
        open={!!selectedAssetForChecks} 
        onClose={() => setSelectedAssetForChecks(null)} 
        maxWidth="xl" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedAssetForChecks?.asset_name} — All Checks
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {selectedAssetForChecks && (
            <Box sx={{ p: 2 }}>
              <CheckResponses extraQuery={{ asset_id: selectedAssetForChecks.asset_id }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAssetForChecks(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Asset dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth={editingAsset ? 'lg' : 'md'}
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
      >
        <DialogTitle sx={{ pb: editingAsset ? 1 : undefined }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
            {editingAsset ? 'Edit Asset' : 'Add New Asset'}
          </Typography>
          {editingAsset && (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 400 }}>
              {[formData.asset_name || editingAsset.asset_name, editingAsset.asset_type_name, editingAsset.location_name]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </DialogTitle>
        {editingAsset && (
          <Tabs
            value={assetDialogTab}
            onChange={(_, v: AssetDialogTab) => setAssetDialogTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              ...pageTabsSx,
              px: { xs: 2, sm: 3 },
              mb: 0,
              minHeight: 48,
            }}
          >
            <Tab value="details" label="Details" />
            <Tab value="people" label="People & fields" />
            <Tab value="files" label="Photos & files" />
            {allowKeyDocuments && <Tab value="documents" label="Key documents" />}
            <Tab value="settings" label="Settings" />
          </Tabs>
        )}
        <DialogContent
          dividers
          sx={{
            pt: 2,
            px: { xs: 2, sm: 3 },
            minHeight: editingAsset ? 400 : undefined,
            maxHeight: editingAsset ? 'min(70vh, 720px)' : 'min(85vh, 800px)',
          }}
        >
          <Grid container spacing={2}>
            {(!editingAsset || assetDialogTab === 'details') && (
            <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Asset Name"
                name="asset_name"
                value={formData.asset_name}
                onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Asset Type</InputLabel>
                <Select
                  name="asset_type_id"
                  value={formData.asset_type_id}
                  onChange={(e) => {
                    const newAssetTypeId = e.target.value;
                    const selectedAssetType = assetTypes.find(at => at.asset_type_id === newAssetTypeId);
                    // Clear recorded_hours if the new asset type doesn't support it
                    const newFormData = { ...formData, asset_type_id: newAssetTypeId };
                    if (!selectedAssetType?.has_recorded_hours) {
                      newFormData.recorded_hours = '';
                    }
                    setFormData(newFormData);
                  }}
                >
                  {assetTypes.map((type) => (
                    <MenuItem key={type.asset_type_id} value={type.asset_type_id}>
                      {type.type_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Home Location</InputLabel>
                <Select
                  name="location_id"
                  value={formData.location_id}
                  label="Home Location"
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.locationId} value={location.locationId}>
                      {location.locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Serial Number"
                name="serial_number"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="QR Code Identifier (optional)"
                name="qr_code"
                value={formData.qr_code || ''}
                onChange={(e) => setFormData({ ...formData, qr_code: e.target.value })}
                helperText="Custom identifier for the QR code. If set, this is used instead of the serial number."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="3rd Party QR Code"
                name="third_party_qr"
                value={formData.third_party_qr || ''}
                onChange={(e) => setFormData({ ...formData, third_party_qr: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                name="manufacturer"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                name="model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                <DatePicker
                  label="Purchase Date"
                  format="dd/MM/yyyy"
                  value={
                    formData.purchase_date
                      ? (() => {
                          const [y, m, d] = formData.purchase_date.split('-').map(Number);
                          return new Date(y, m - 1, d);
                        })()
                      : null
                  }
                  onChange={(date) => {
                    if (date && !isNaN((date as Date).getTime())) {
                      const dt = date as Date;
                      const y = dt.getFullYear();
                      const m = String(dt.getMonth() + 1).padStart(2, '0');
                      const d = String(dt.getDate()).padStart(2, '0');
                      setFormData({ ...formData, purchase_date: `${y}-${m}-${d}` });
                    } else {
                      setFormData({ ...formData, purchase_date: '' });
                    }
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Number Plate"
                name="number_plate"
                value={formData.number_plate}
                onChange={(e) => setFormData({ ...formData, number_plate: e.target.value })}
                helperText="MOT and tax status will be checked automatically when a number plate is saved"
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
              {editingAsset && editingAsset.number_plate && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<HistoryIcon fontSize="small" />}
                  sx={{ mt: 0.5, fontSize: '0.75rem' }}
                  onClick={() => openMotHistory(editingAsset)}
                >
                  View MOT History
                </Button>
              )}
            </Grid>
            {/* Only show Hours / Mileage if enabled for this asset type */}
            {(() => {
              // Show recorded hours if:
              // 1. We're editing an asset that has recorded hours enabled
              // 2. OR the selected asset type has recorded hours enabled
              const selectedAssetType = assetTypes.find(at => at.asset_type_id === formData.asset_type_id);
              const shouldShowRecordedHours = 
                (editingAsset && editingAsset.has_recorded_hours) || 
                (selectedAssetType && selectedAssetType.has_recorded_hours);
              
              logger.debug('🔍 Should show recorded hours field?', {
                shouldShow: shouldShowRecordedHours,
                editingAsset_has_recorded_hours: editingAsset?.has_recorded_hours,
                selectedAssetType_has_recorded_hours: selectedAssetType?.has_recorded_hours,
                formData_asset_type_id: formData.asset_type_id,
                formData_recorded_hours: formData.recorded_hours
              });
              
              return shouldShowRecordedHours ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hours / Mileage"
                    name="recorded_hours"
                    type="number"
                    value={formData.recorded_hours}
                    onChange={(e) => setFormData({ ...formData, recorded_hours: e.target.value })}
                    helperText="Total hours or mileage recorded for this asset"
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Grid>
              ) : null;
            })()}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            </>
            )}

            {(!editingAsset || assetDialogTab === 'people') && (
            <>
            {/* Asset Tags — show all tags including asset-scope ones (e.g. "Attachments"); user-scope tags are excluded */}
            {jobTags.length > 0 && (
              <Grid item xs={12}>
                <Autocomplete<JobTag, true, false, false>
                  multiple
                  id="asset-job-tags"
                  size="small"
                  options={jobTags}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.tag_name}
                  isOptionEqualToValue={(a, b) => a.job_tag_id === b.job_tag_id}
                  value={jobTags.filter((t) => (formData.job_tag_ids || []).some((id) => String(id) === String(t.job_tag_id)))}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      job_tag_ids: newValue.map((t) => String(t.job_tag_id)),
                    });
                  }}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...other } = props as { key: string } & React.HTMLAttributes<HTMLLIElement>;
                    const isAttachmentTag = option.tag_scope === 'asset' && option.tag_name.toLowerCase() === 'attachments';
                    return (
                      <Box
                        key={key}
                        component="li"
                        {...other}
                        sx={{
                          display: 'flex !important',
                          alignItems: 'flex-start !important',
                          gap: 1,
                          py: 0.75,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-of-type': { borderBottom: 'none' },
                        }}
                      >
                        <Checkbox size="small" checked={selected} sx={{ p: 0, mt: 0.25 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                              {option.tag_name}
                            </Typography>
                            {isAttachmentTag && (
                              <Chip label="Attachments" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }} />
                            )}
                          </Box>
                          {option.description ? (
                            <Typography
                              component="div"
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}
                            >
                              {option.description}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const tagProps = getTagProps({ index });
                      const isAttachment = option.tag_scope === 'asset' && option.tag_name.toLowerCase() === 'attachments';
                      return (
                        <Chip
                          {...tagProps}
                          key={option.job_tag_id}
                          size="small"
                          label={option.tag_name}
                          color={isAttachment ? 'success' : 'primary'}
                          variant="outlined"
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Asset tags"
                      placeholder="Search or pick tags…"
                    />
                  )}
                  noOptionsText="No tags match your search"
                  ListboxProps={{
                    style: { maxHeight: 220, padding: 0 },
                  }}
                  componentsProps={{
                    popper: {
                      placement: 'top-start',
                      sx: {
                        zIndex: (theme) => theme.zIndex.modal + 2,
                        '& .MuiAutocomplete-listbox': { py: 0 },
                      },
                    },
                    paper: {
                      elevation: 8,
                      sx: { border: '1px solid', borderColor: 'divider' },
                    },
                  }}
                />
              </Grid>
            )}

            {/* Primary User */}
            <Grid item xs={12}>
              <Autocomplete
                id="primary-user"
                size="small"
                options={businessUsers}
                getOptionLabel={(u) => {
                  const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
                  if (u.isSharedAccountMember) {
                    const acct = u.sharedAccountName || u.email || 'Shared account';
                    return name ? `${name} (shared: ${acct})` : `${u.email} (shared member)`;
                  }
                  return name ? `${name} (${u.email})` : u.email;
                }}
                isOptionEqualToValue={(a, b) => a.userId === b.userId}
                value={
                  businessUsers.find(
                    (u) =>
                      u.userId === (formData.primary_member_id || formData.primary_user_id || '')
                  ) || null
                }
                onChange={(_, newValue) => {
                  if (!newValue) {
                    setFormData({
                      ...formData,
                      primary_user_id: null,
                      primary_member_id: null,
                      primary_member_name: null,
                    });
                    return;
                  }
                  if (newValue.isSharedAccountMember) {
                    const nm = [newValue.firstName, newValue.lastName].filter(Boolean).join(' ').trim() || null;
                    setFormData({
                      ...formData,
                      primary_user_id: null,
                      primary_member_id: newValue.userId,
                      primary_member_name: nm,
                    });
                  } else {
                    setFormData({
                      ...formData,
                      primary_user_id: newValue.userId,
                      primary_member_id: null,
                      primary_member_name: null,
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Primary User (optional)"
                    placeholder="Search for a user…"
                    helperText="The person primarily responsible for this asset"
                  />
                )}
                noOptionsText="No users found"
                componentsProps={{
                  popper: {
                    placement: 'top-start',
                    sx: { zIndex: (theme: any) => theme.zIndex.modal + 2 },
                  },
                }}
              />
            </Grid>

            {/* Custom Fields Section */}
            {customFields.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1, ...sectionTitleSx }}>
                    Additional information
                  </Typography>
                </Grid>
                {customFields.map((field) => (
                  <Grid item xs={12} sm={6} key={field.custom_field_id}>
                    {field.field_type === 'text' && (
                      <TextField
                        fullWidth
                        label={field.field_label}
                        value={customFieldValues[field.custom_field_id] || ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const value =
                            field.field_name === 'whole_good_number'
                              ? normalizeWholeGoodNumber(raw)
                              : raw;
                          setCustomFieldValues({
                            ...customFieldValues,
                            [field.custom_field_id]: value,
                          });
                        }}
                        required={field.is_required}
                        placeholder={field.placeholder}
                        helperText={field.help_text}
                      />
                    )}
                    {field.field_type === 'textarea' && (
                      <TextField
                        fullWidth
                        label={field.field_label}
                        value={customFieldValues[field.custom_field_id] || ''}
                        onChange={(e) => setCustomFieldValues({ 
                          ...customFieldValues, 
                          [field.custom_field_id]: e.target.value 
                        })}
                        required={field.is_required}
                        placeholder={field.placeholder}
                        helperText={field.help_text}
                        multiline
                        rows={3}
                      />
                    )}
                    {field.field_type === 'number' && (
                      <TextField
                        fullWidth
                        type="number"
                        label={field.field_label}
                        value={customFieldValues[field.custom_field_id] || ''}
                        onChange={(e) => setCustomFieldValues({ 
                          ...customFieldValues, 
                          [field.custom_field_id]: e.target.value 
                        })}
                        required={field.is_required}
                        placeholder={field.placeholder}
                        helperText={field.help_text}
                      />
                    )}
                    {field.field_type === 'date' && (
                      <TextField
                        fullWidth
                        type="date"
                        label={field.field_label}
                        value={customFieldValues[field.custom_field_id] || ''}
                        onChange={(e) => setCustomFieldValues({ 
                          ...customFieldValues, 
                          [field.custom_field_id]: e.target.value 
                        })}
                        required={field.is_required}
                        helperText={field.help_text}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    {field.field_type === 'select' && field.field_options && (
                      <FormControl fullWidth required={field.is_required}>
                        <InputLabel>{field.field_label}</InputLabel>
                        <Select
                          value={customFieldValues[field.custom_field_id] || ''}
                          onChange={(e) => setCustomFieldValues({ 
                            ...customFieldValues, 
                            [field.custom_field_id]: e.target.value 
                          })}
                        >
                          <MenuItem value="">
                            <em>Select an option</em>
                          </MenuItem>
                          {field.field_options.map((option, idx) => (
                            <MenuItem key={idx} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {field.help_text && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                            {field.help_text}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                    {field.field_type === 'checkbox' && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={customFieldValues[field.custom_field_id] === 'true' || customFieldValues[field.custom_field_id] === '1'}
                            onChange={(e) => setCustomFieldValues({ 
                              ...customFieldValues, 
                              [field.custom_field_id]: e.target.checked ? 'true' : 'false'
                            })}
                          />
                        }
                        label={
                          <Box>
                            <Typography>{field.field_label}</Typography>
                            {field.help_text && (
                              <Typography variant="caption" color="text.secondary">
                                {field.help_text}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    )}
                  </Grid>
                ))}
              </>
            )}
            </>
            )}

            {(!editingAsset || assetDialogTab === 'files') && (
            <>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={sectionTitleSx}>Photos &amp; files</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* Legacy single-image upload (still sets photo_url) */}
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="upload-asset-image"
                    type="file"
                    onChange={handleFileSelected}
                    ref={fileInputRef}
                    disabled={uploadingImage || uploadingAttachment}
                  />
                  {/* Multi-file attachment upload */}
                  <input
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                    style={{ display: 'none' }}
                    id="upload-asset-attachment"
                    type="file"
                    multiple
                    onChange={handleAttachmentFileSelected}
                    ref={attachmentFileInputRef}
                    disabled={uploadingImage || uploadingAttachment}
                  />
                  <label htmlFor="upload-asset-attachment">
                    <Button
                      variant="contained"
                      component="span"
                      size="small"
                      startIcon={(uploadingImage || uploadingAttachment) ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                      disabled={uploadingImage || uploadingAttachment}
                    >
                      {(uploadingImage || uploadingAttachment) ? 'Uploading...' : 'Add Files'}
                    </Button>
                  </label>
                </Box>
              </Box>

              {!editingAsset && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Save the asset first, then you can add photos and documents.
                </Typography>
              )}

              {/* Attachment gallery */}
              {attachments.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1, mb: 2 }}>
                  {attachments.map((att) => {
                    const isMainImage = att.file_type === 'image' && !!formData.photo_url && formData.photo_url === att.file_url;
                    return (
                    <Box
                      key={att.attachment_id}
                      sx={{
                        position: 'relative',
                        width: 100,
                        height: 100,
                        borderRadius: 2,
                        overflow: 'visible',
                        border: isMainImage ? '2px solid' : '1px solid',
                        borderColor: isMainImage ? 'warning.main' : 'divider',
                      }}
                    >
                      {att.file_type === 'image' ? (
                        <img
                          src={att.file_url.startsWith('blob:') ? att.file_url : getImageUrl(att.file_url)}
                          alt={att.file_name}
                          onError={handleImageError}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                        />
                      ) : (
                        <Box sx={{
                          width: '100%', height: '100%', borderRadius: 2,
                          bgcolor: att.file_type === 'pdf' ? '#fff5f5' : '#f5f5ff',
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                          p: 0.5, gap: 0.5,
                        }}>
                          <Typography sx={{ fontSize: 28 }}>
                            {att.file_type === 'pdf' ? '📄' : '📎'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.2, maxHeight: 36, overflow: 'hidden' }}
                          >
                            {att.file_name}
                          </Typography>
                        </Box>
                      )}
                      {/* Open file in new tab */}
                      {!att.attachment_id.startsWith('temp_') && att.file_type !== 'image' && (
                        <Tooltip title="Open file">
                          <IconButton
                            size="small"
                            component="a"
                            href={att.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              position: 'absolute', top: -10, left: -10,
                              bgcolor: 'background.paper', border: '1px solid',
                              borderColor: 'divider', p: '2px',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Typography sx={{ fontSize: 12 }}>↗</Typography>
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Delete button */}
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteAttachment(att)}
                        sx={{
                          position: 'absolute', top: -10, right: -10,
                          bgcolor: 'background.paper', border: '1px solid',
                          borderColor: 'divider', p: '2px',
                          color: 'error.main',
                          '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      {/* Set as main image */}
                      {att.file_type === 'image' && (
                        <Tooltip title={isMainImage ? 'Main image' : 'Set as main image'}>
                          <Box
                            component={isMainImage ? 'div' : 'button'}
                            {...(isMainImage ? {} : {
                              onClick: () => handleSetMainAttachment(att),
                              disabled: settingMainAttachmentId === att.attachment_id,
                            })}
                            sx={{
                              position: 'absolute', bottom: 4, left: 4, right: 4,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                              bgcolor: isMainImage ? 'warning.main' : 'rgba(0,0,0,0.55)',
                              color: '#fff',
                              borderRadius: 1,
                              border: 'none',
                              py: '2px',
                              cursor: isMainImage ? 'default' : 'pointer',
                              fontSize: 10,
                              fontWeight: 600,
                              lineHeight: 1.4,
                              '&:hover': isMainImage ? {} : { bgcolor: 'rgba(0,0,0,0.75)' },
                            }}
                          >
                            {settingMainAttachmentId === att.attachment_id ? (
                              <CircularProgress size={10} sx={{ color: '#fff' }} />
                            ) : isMainImage ? (
                              <StarIcon sx={{ fontSize: 12 }} />
                            ) : (
                              <StarBorderIcon sx={{ fontSize: 12 }} />
                            )}
                            {isMainImage ? 'Main' : 'Set main'}
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    );
                  })}
                </Box>
              )}

              {attachments.length === 0 && editingAsset && (
                <Box
                  sx={{
                    border: '2px dashed', borderColor: 'divider',
                    borderRadius: 2, p: 3,
                    textAlign: 'center', color: 'text.secondary',
                    cursor: 'pointer',
                  }}
                  onClick={() => attachmentFileInputRef.current?.click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                  <Typography variant="body2">Click to add photos, PDFs or documents</Typography>
                </Box>
              )}

              {/* Legacy single-image preview (for backward compat, only when the main image isn't in the gallery above) */}
              {formData.photo_url && !attachments.some(a => a.file_url === formData.photo_url) && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">Main image (used for thumbnail)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <img
                      src={getImageUrl(formData.photo_url)}
                      alt="Asset preview"
                      style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }}
                      onError={handleImageError}
                    />
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      onClick={() => setFormData(prev => ({ ...prev, photo_url: '' }))}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              )}
            </Grid>
            </>
            )}

            {/* ── Key Documents & RAMS ── */}
            {editingAsset && allowKeyDocuments && assetDialogTab === 'documents' && (
              <Grid item xs={12}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" sx={sectionTitleSx}>
                      Key documents &amp; RAMS
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" onClick={() => setAddFolderDialogOpen(true)}>
                        + Folder
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => setAddDocDialogOpen(true)}>
                        + Upload Document
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => { setNewTextDocForm({ document_type: 'other', title: '', folder_id: '', issue_date: '', expiry_date: '', notes: '', content: '' }); setNewTextDocDialogOpen(true); }}>
                        + New Text Doc
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        startIcon={aiAnalysisLoading ? <CircularProgress size={14} color="inherit" /> : null}
                        onClick={handleRunAiAnalysis}
                        disabled={aiAnalysisLoading}
                      >
                        {aiAnalysisLoading ? 'Analysing…' : 'AI Analysis'}
                      </Button>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                    Powered by Policy Max
                  </Typography>

                  {/* AI Analysis Result */}
                  {aiAnalysis && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: aiAnalysis.overall_status === 'COMPLIANT' ? 'success.50' : aiAnalysis.overall_status === 'NON_COMPLIANT' ? 'error.50' : 'warning.50', borderRadius: 2, border: 1, borderColor: aiAnalysis.overall_status === 'COMPLIANT' ? 'success.200' : aiAnalysis.overall_status === 'NON_COMPLIANT' ? 'error.200' : 'warning.200' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          label={aiAnalysis.overall_status.replace('_', ' ')}
                          color={aiAnalysis.overall_status === 'COMPLIANT' ? 'success' : aiAnalysis.overall_status === 'NON_COMPLIANT' ? 'error' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{aiAnalysis.summary}</Typography>
                      </Box>
                      {aiAnalysis.required_documents?.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Required documents</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {aiAnalysis.required_documents.map((rd, i) => (
                              <Chip
                                key={i}
                                size="small"
                                label={rd.label}
                                color={rd.status === 'present' ? 'success' : rd.status === 'expiring_soon' ? 'warning' : rd.status === 'expired' ? 'error' : 'default'}
                                variant={rd.status === 'missing' ? 'outlined' : 'filled'}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {aiAnalysis.action_items?.length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Actions needed</Typography>
                          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                            {aiAnalysis.action_items.map((a, i) => (
                              <Typography key={i} component="li" variant="caption">{a}</Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                      <Button size="small" sx={{ mt: 1 }} onClick={() => setAiAnalysis(null)}>Dismiss</Button>
                    </Box>
                  )}

                  {loadingDocs ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
                  ) : (assetDocuments.length === 0 && docFolders.length === 0) ? (
                    <Box
                      sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center', color: 'text.secondary', cursor: 'pointer' }}
                      onClick={() => setAddDocDialogOpen(true)}
                    >
                      <Typography variant="body2" sx={{ mb: 0.5 }}>No documents yet</Typography>
                      <Typography variant="caption">Click &quot;+ Upload Document&quot; to add a LOLER cert, RAMS, insurance or any compliance document</Typography>
                    </Box>
                  ) : (
                    <Box>
                      {/* ── Folders (always shown, even when empty) ── */}
                      {docFolders.map(folder => {
                        const docs = assetDocuments.filter(d => d.folder_id === folder.folder_id);
                        return (
                          <Box key={folder.folder_id} sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                            {/* Folder header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: 'grey.50', borderBottom: docs.length > 0 ? 1 : 0, borderColor: 'divider' }}>
                              <Typography sx={{ fontSize: 16 }}>📁</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>{folder.folder_name}</Typography>
                              <Chip size="small" label={`${docs.length} doc${docs.length !== 1 ? 's' : ''}`} sx={{ fontSize: '0.65rem', height: 18 }} />
                              <Tooltip title="Upload to this folder">
                                <IconButton size="small" onClick={() => { setNewDocForm(p => ({ ...p, folder_id: folder.folder_id })); setAddDocDialogOpen(true); }}>
                                  <AddIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              {folder.folder_name !== 'Check reports' && (
                                <Tooltip title="Rename folder">
                                  <IconButton size="small" onClick={() => openRenameFolder(folder.folder_id, folder.folder_name)}>
                                    <EditIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Delete folder">
                                <IconButton size="small" color="error" onClick={() => handleDeleteFolder(folder.folder_id)} sx={{ p: 0.25 }}>
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {/* Folder contents */}
                            {docs.length === 0 ? (
                              <Box
                                sx={{ p: 2, textAlign: 'center', color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                onClick={() => { setNewDocForm(p => ({ ...p, folder_id: folder.folder_id })); setAddDocDialogOpen(true); }}
                              >
                                <Typography variant="caption">Empty folder — click + to add a document</Typography>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                {docs.map((doc, idx) => (
                                  <DocRow key={doc.document_id} doc={doc} idx={idx} total={docs.length}
                                    onDelete={() => handleDeleteDocument(doc)}
                                    onReview={() => handleRunDocumentReview(editingAsset!.asset_id, doc)}
                                    reviewLoading={!!docReviewLoading[doc.document_id]}
                                    review={docReviews[doc.document_id] || null}
                                    onDismissReview={() => setDocReviews(prev => { const n = { ...prev }; delete n[doc.document_id]; return n; })}
                                    onRename={() => openRenameDocument(doc)}
                                    onEdit={(doc.file_type === 'text' || doc.mime_type === 'text/plain') ? () => handleOpenTextEditor(editingAsset!.asset_id, doc) : undefined}
                                    docTypeLabel={docTypeLabel}
                                    docExpiryStatus={docExpiryStatus}
                                    expiryChipProps={expiryChipProps}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                      })}

                      {/* ── Unfoldered documents ── */}
                      {(() => {
                        const unfiled = assetDocuments.filter(d => !d.folder_id);
                        if (unfiled.length === 0) return null;
                        return (
                          <Box sx={{ mb: 1 }}>
                            {docFolders.length > 0 && (
                              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 0.75 }}>
                                Unfoldered
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              {unfiled.map(doc => (
                                <DocRow key={doc.document_id} doc={doc}
                                  onDelete={() => handleDeleteDocument(doc)}
                                  onReview={() => handleRunDocumentReview(editingAsset!.asset_id, doc)}
                                  reviewLoading={!!docReviewLoading[doc.document_id]}
                                  review={docReviews[doc.document_id] || null}
                                  onDismissReview={() => setDocReviews(prev => { const n = { ...prev }; delete n[doc.document_id]; return n; })}
                                  onRename={() => openRenameDocument(doc)}
                                  onEdit={(doc.file_type === 'text' || doc.mime_type === 'text/plain') ? () => handleOpenTextEditor(editingAsset!.asset_id, doc) : undefined}
                                  docTypeLabel={docTypeLabel}
                                  docExpiryStatus={docExpiryStatus}
                                  expiryChipProps={expiryChipProps}
                                />
                              ))}
                            </Box>
                          </Box>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            {(!editingAsset || assetDialogTab === 'settings') && (
            <>
            {/* Out of Action Section - Only show when editing */}
            {editingAsset && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ pt: editingAsset ? 0 : 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, ...sectionTitleSx }}>
                      Out of Action Status
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Mark this asset as out of action to prevent it from being loaned or delivered. An email notification will be sent to account emails.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.is_out_of_action || false}
                        onChange={(e) => setFormData({ ...formData, is_out_of_action: e.target.checked })}
                        color="error"
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Mark as Out of Action
                      </Typography>
                    }
                  />
                </Grid>
                {formData.is_out_of_action && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Reason/Note"
                      value={formData.out_of_action_note || ''}
                      onChange={(e) => setFormData({ ...formData, out_of_action_note: e.target.value })}
                      placeholder="Enter a note explaining why this asset is out of action (e.g., 'Damaged during return', 'Requires maintenance', etc.)"
                      helperText="This note will be visible to users and sent via email notification"
                      multiline
                      rows={3}
                    />
                  </Grid>
                )}
              </>
            )}

            {/* Enable for offline - show for both add and edit */}
            <Grid item xs={12}>
              <Box sx={{ borderTop: editingAsset ? 1 : 0, borderColor: 'divider', pt: editingAsset ? 2 : 0, mt: editingAsset ? 2 : 0 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, ...sectionTitleSx }}>
                  Offline access
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.enabled_for_offline || false}
                      onChange={(e) => setFormData({ ...formData, enabled_for_offline: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Enable for offline
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                  When enabled, this asset and its check templates will be downloaded for use without internet.
                </Typography>
              </Box>
            </Grid>
            </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.asset_name || !formData.asset_type_id || !formData.location_id || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Saving...' : (editingAsset ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Text Editor dialog ── */}
      <Dialog open={allowKeyDocuments && textEditorOpen} onClose={() => setTextEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📝 {textEditorDoc?.title || 'Text Document'}
          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>— edit & save</Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {textEditorLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <TextField
              multiline
              fullWidth
              minRows={18}
              maxRows={32}
              value={textEditorContent}
              onChange={e => setTextEditorContent(e.target.value)}
              placeholder="Start typing your document content here…"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 } }}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
            {textEditorContent.length.toLocaleString()} chars · {textEditorContent.split('\n').length} lines
          </Typography>
          <Button onClick={() => setTextEditorOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={textEditorLoading || textEditorSaving}
            startIcon={textEditorSaving ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={() => handleSaveTextEditor(editingAsset!.asset_id)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── New Text Document dialog ── */}
      <Dialog open={allowKeyDocuments && newTextDocDialogOpen} onClose={() => setNewTextDocDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Text Document</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Title *" fullWidth size="small"
              value={newTextDocForm.title}
              onChange={e => setNewTextDocForm(p => ({ ...p, title: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Document Type" size="small" sx={{ flex: 1 }}
                value={newTextDocForm.document_type}
                onChange={e => setNewTextDocForm(p => ({ ...p, document_type: e.target.value }))}>
                {docTypeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField select label="Folder (optional)" size="small" sx={{ flex: 1 }}
                value={newTextDocForm.folder_id}
                onChange={e => setNewTextDocForm(p => ({ ...p, folder_id: e.target.value }))}>
                <MenuItem value="">No folder</MenuItem>
                {docFolders.map(f => <MenuItem key={f.folder_id} value={f.folder_id}>{f.folder_name}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Issue Date" type="date" size="small" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
                value={newTextDocForm.issue_date}
                onChange={e => setNewTextDocForm(p => ({ ...p, issue_date: e.target.value }))} />
              <TextField label="Expiry Date" type="date" size="small" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
                value={newTextDocForm.expiry_date}
                onChange={e => setNewTextDocForm(p => ({ ...p, expiry_date: e.target.value }))} />
            </Box>
            <TextField label="Notes" multiline rows={2} fullWidth size="small"
              value={newTextDocForm.notes}
              onChange={e => setNewTextDocForm(p => ({ ...p, notes: e.target.value }))} />
            <TextField
              label="Document content"
              multiline
              minRows={14}
              fullWidth
              placeholder="Type or paste your document content here — RAMS, method statement, notes, etc."
              value={newTextDocForm.content}
              onChange={e => setNewTextDocForm(p => ({ ...p, content: e.target.value }))}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
            {newTextDocForm.content.length.toLocaleString()} chars
          </Typography>
          <Button onClick={() => setNewTextDocDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newTextDocForm.title.trim()}
            onClick={() => handleCreateTextDoc(editingAsset!.asset_id)}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Document dialog ── */}
      <Dialog open={allowKeyDocuments && addDocDialogOpen} onClose={() => { setAddDocDialogOpen(false); setNewDocForm({ document_type: 'other', title: '', folder_id: '', issue_date: '', expiry_date: '', notes: '', file: null }); }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Document Type</InputLabel>
                <Select value={newDocForm.document_type} label="Document Type" onChange={e => setNewDocForm(p => ({ ...p, document_type: e.target.value }))}>
                  {docTypeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Title" required value={newDocForm.title} onChange={e => setNewDocForm(p => ({ ...p, title: e.target.value }))} />
            </Grid>
            {docFolders.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Folder (optional)</InputLabel>
                  <Select value={newDocForm.folder_id} label="Folder (optional)" onChange={e => setNewDocForm(p => ({ ...p, folder_id: e.target.value }))}>
                    <MenuItem value=""><em>No folder</em></MenuItem>
                    {docFolders.map(f => <MenuItem key={f.folder_id} value={f.folder_id}>{f.folder_name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Issue Date" type="date" InputLabelProps={{ shrink: true }} value={newDocForm.issue_date} onChange={e => setNewDocForm(p => ({ ...p, issue_date: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Expiry Date" type="date" InputLabelProps={{ shrink: true }} value={newDocForm.expiry_date} onChange={e => setNewDocForm(p => ({ ...p, expiry_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes (optional)" multiline rows={2} value={newDocForm.notes} onChange={e => setNewDocForm(p => ({ ...p, notes: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <input type="file" ref={docFileInputRef} style={{ display: 'none' }}
                accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.txt"
                onChange={e => { const f = e.target.files?.[0]; if (f) { setNewDocForm(p => ({ ...p, file: f, title: p.title || f.name.replace(/\.[^.]+$/, '') })); } }}
              />
              <Button variant="outlined" fullWidth startIcon={<CloudUploadIcon />} onClick={() => docFileInputRef.current?.click()}>
                {newDocForm.file ? newDocForm.file.name : 'Choose File'}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDocDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadDocument}
            disabled={!newDocForm.file || !newDocForm.title.trim() || uploadingDoc}
            startIcon={uploadingDoc ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {uploadingDoc ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Folder dialog ── */}
      <Dialog open={allowKeyDocuments && renameDialogOpen} onClose={() => !renameSaving && setRenameDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{renameTarget?.type === 'folder' ? 'Rename folder' : 'Rename file'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={renameTarget?.type === 'folder' ? 'Folder name' : 'Display name'}
            value={renameName}
            onChange={e => setRenameName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && renameName.trim()) handleSaveRename(); }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)} disabled={renameSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRename} disabled={!renameName.trim() || renameSaving}>
            {renameSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={allowKeyDocuments && addFolderDialogOpen} onClose={() => { setAddFolderDialogOpen(false); setNewFolderName(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Create Folder</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Folder name" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} sx={{ mt: 1 }} onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFolderDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFolder} disabled={!newFolderName.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Asset Type dialog */}
      <Dialog
        open={openAssetTypeDialog}
        onClose={handleCloseAssetTypeDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingAssetType ? 'Edit Asset Type' : 'Add New Asset Type'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Type Name"
                value={assetTypeFormData.type_name}
                onChange={(e) => setAssetTypeFormData({ ...assetTypeFormData, type_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={assetTypeFormData.description}
                onChange={(e) => setAssetTypeFormData({ ...assetTypeFormData, description: e.target.value })}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={assetTypeFormData.requires_car_registration}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAssetTypeFormData({ ...assetTypeFormData, requires_car_registration: e.target.checked })}
                    name="requires_car_registration"
                    color="primary"
                  />
                }
                label="Requires Vehicle Registration"
              />
            </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={assetTypeFormData.has_whole_good_number}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAssetTypeFormData({
                      ...assetTypeFormData,
                      has_whole_good_number: e.target.checked,
                    })
                  }
                  name="has_whole_good_number"
                  color="primary"
                />
              }
              label="Enable Wholegood Number field (WGS-prefixed)"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={assetTypeFormData.has_recorded_hours}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAssetTypeFormData({
                      ...assetTypeFormData,
                      has_recorded_hours: e.target.checked,
                    })
                  }
                  name="has_recorded_hours"
                  color="primary"
                />
              }
              label="Enable hours / mileage tracking for this asset type"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={assetTypeFormData.mark_out_of_action_on_fail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAssetTypeFormData({
                      ...assetTypeFormData,
                      mark_out_of_action_on_fail: e.target.checked,
                    })
                  }
                  name="mark_out_of_action_on_fail"
                  color="primary"
                />
              }
              label="Mark asset out of action when a check fails"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
              When enabled, assets of this type are automatically marked out of action if a safety check fails. Disable for asset types where a failed check should be recorded but the asset can still be used.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={assetTypeFormData.allow_loan_attachments}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAssetTypeFormData({
                      ...assetTypeFormData,
                      allow_loan_attachments: e.target.checked,
                    })
                  }
                  name="allow_loan_attachments"
                  color="primary"
                />
              }
              label="Allow attachments for this asset type"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
              When enabled, assets of this type can be selected as optional attachments on loan and delivery checks in the mobile app (e.g. implements, trailers).
            </Typography>
          </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={handleCloseAssetTypeDialog}>Cancel</Button>
          <Button onClick={handleAssetTypeSubmit} variant="contained">
            {editingAssetType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={handleCloseQrDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {qrData ? `${qrData.assetName} — Asset QR Code` : 'QR Code'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
            Scan with your phone's camera app - it will automatically open the asset in Check Now
          </Typography>
          {qrData && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2 }}>
              <Typography variant="caption" color="text.disabled">
                {qrData.identifierLabel}:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>
                {qrData.identifierValue}
              </Typography>
            </Box>
          )}
          <Box id="qr-code-container" sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
            {qrData && (
              <QRCode value={qrData.url} size={220} level="M" />
            )}
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', fontFamily: 'monospace', wordBreak: 'break-all', px: 2 }}>
            {qrData?.url}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQrDialog}>Close</Button>
          <Button onClick={handleDownloadQr} variant="outlined">Download SVG</Button>
          <Button onClick={handlePrintQr} variant="contained">Print</Button>
        </DialogActions>
      </Dialog>

      {/* Asset Unlock Dialog */}
      <Dialog
        open={unlockDialog.open}
        onClose={() => !unlockDialog.unlocking && setUnlockDialog({ open: false, asset: null, checkResponses: [], loadingResponses: false, unlocking: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f3e5f5', color: '#4A148C', fontWeight: 700 }}>
          Check In Progress — {unlockDialog.asset?.asset_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {unlockDialog.asset && (
            <Box>
              <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #e0e0e0', mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Locked by:</Typography>
                <Typography variant="body1">{unlockDialog.asset.locked_by_name}</Typography>
                {unlockDialog.asset.locked_at && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Since: {new Date(unlockDialog.asset.locked_at).toLocaleString()}
                  </Typography>
                )}
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Partial Answers Entered:
              </Typography>
              {unlockDialog.loadingResponses ? (
                <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
              ) : unlockDialog.checkResponses.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  No answers saved yet — the user has not answered any questions, or answers are still being synced to the server.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                  {unlockDialog.checkResponses.map((r: any, i: number) => (
                    <Box key={i} sx={{ mb: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '3px solid #7B1FA2' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {r.question_text || `Question ${i + 1}`}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                        {r.response_text}
                      </Typography>
                      {r.created_at && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          {r.user_name} — {new Date(r.created_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              <Alert severity="warning" sx={{ mt: 1 }}>
                Unlocking will mark this check as <strong>discarded</strong> and remove the user's access to it. Any answers they entered will be saved and viewable under "Show Discarded Checks" in the check responses.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUnlockDialog({ open: false, asset: null, checkResponses: [], loadingResponses: false, unlocking: false })}
            disabled={unlockDialog.unlocking}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleUnlockAsset}
            disabled={unlockDialog.unlocking}
          >
            {unlockDialog.unlocking ? <CircularProgress size={20} color="inherit" /> : 'Unlock & Discard Check'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Out of Action Dialog */}
      <Dialog
        open={outOfActionDialog.open}
        onClose={() => setOutOfActionDialog({ open: false, asset: null, note: '', isOutOfAction: false, images: [], uploadingImages: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {outOfActionDialog.isOutOfAction ? 'Mark Asset as Available' : 'Mark Asset as Out of Action'}
        </DialogTitle>
        <DialogContent>
          {outOfActionDialog.asset && (
            <>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                Asset: <strong>{outOfActionDialog.asset.asset_name}</strong>
              </Typography>
              {!outOfActionDialog.isOutOfAction && (
                <>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Reason/Note"
                    value={outOfActionDialog.note}
                    onChange={(e) => setOutOfActionDialog({ ...outOfActionDialog, note: e.target.value })}
                    placeholder="Enter a note explaining why this asset is out of action (e.g., 'Damaged during return', 'Requires maintenance', etc.)"
                    helperText="Required. This note will be visible to users and sent via email notification."
                    required
                    sx={{ mt: 2 }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="out-of-action-mark-image-upload"
                      multiple
                      type="file"
                      onChange={(e) => {
                        if (e.target.files) {
                          const newImages = Array.from(e.target.files);
                          setOutOfActionDialog({
                            ...outOfActionDialog,
                            images: [...outOfActionDialog.images, ...newImages]
                          });
                        }
                      }}
                    />
                    <label htmlFor="out-of-action-mark-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                      >
                        Add image(s) (optional)
                      </Button>
                    </label>
                    {outOfActionDialog.images.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          {outOfActionDialog.images.length} image(s) selected
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {outOfActionDialog.images.map((image, index) => (
                            <Chip
                              key={index}
                              label={image.name}
                              onDelete={() => {
                                const newImages = outOfActionDialog.images.filter((_, i) => i !== index);
                                setOutOfActionDialog({ ...outOfActionDialog, images: newImages });
                              }}
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </>
              )}
              {outOfActionDialog.isOutOfAction && (
                <>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This asset is currently marked as out of action. Provide details about why it's now available again.
                    {outOfActionDialog.asset.out_of_action_note && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Original Out of Action Note:</Typography>
                        <Typography variant="body2">{outOfActionDialog.asset.out_of_action_note}</Typography>
                      </Box>
                    )}
                  </Alert>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Resolution Note"
                    value={outOfActionDialog.note}
                    onChange={(e) => setOutOfActionDialog({ ...outOfActionDialog, note: e.target.value })}
                    placeholder="Explain what was done to make this asset available again (e.g., 'Repaired and tested', 'Maintenance completed', 'Inspection passed', etc.)"
                    helperText="This note is required and will be visible in the audit log"
                    required
                    sx={{ mt: 2 }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="out-of-action-image-upload"
                      multiple
                      type="file"
                      onChange={(e) => {
                        if (e.target.files) {
                          const newImages = Array.from(e.target.files);
                          setOutOfActionDialog({ 
                            ...outOfActionDialog, 
                            images: [...outOfActionDialog.images, ...newImages] 
                          });
                        }
                      }}
                    />
                    <label htmlFor="out-of-action-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                      >
                        Upload Images (Optional)
                      </Button>
                    </label>
                    {outOfActionDialog.images.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          {outOfActionDialog.images.length} image(s) selected
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {outOfActionDialog.images.map((image, index) => (
                            <Chip
                              key={index}
                              label={image.name}
                              onDelete={() => {
                                const newImages = outOfActionDialog.images.filter((_, i) => i !== index);
                                setOutOfActionDialog({ ...outOfActionDialog, images: newImages });
                              }}
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOutOfActionDialog({ open: false, asset: null, note: '', isOutOfAction: false, images: [], uploadingImages: false })}
            disabled={outOfActionDialog.uploadingImages}
          >
            Cancel
          </Button>
          <Button
            onClick={handleOutOfActionSave}
            variant="contained"
            color={outOfActionDialog.isOutOfAction ? "success" : "error"}
            disabled={outOfActionDialog.uploadingImages || !outOfActionDialog.note.trim()}
            startIcon={outOfActionDialog.uploadingImages ? <CircularProgress size={20} /> : undefined}
          >
            {outOfActionDialog.uploadingImages
              ? 'Uploading...'
              : (outOfActionDialog.isOutOfAction ? 'Mark as Available' : 'Mark as Out of Action')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Fields Management Dialog */}
      <Dialog
        open={customFieldsDialog}
        onClose={() => setCustomFieldsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedAssetTypeForCustomFields?.type_name} — Custom Fields
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Add custom fields to collect additional information for this asset type
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCustomFieldFormDialog()}
            >
              Add Field
            </Button>
          </Box>

          {loadingCustomFields ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : customFields.length === 0 ? (
            <Alert severity="info">
              No custom fields defined for this asset type. Add your first field to get started.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Label</TableCell>
                    <TableCell>Field Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customFields.map((field) => (
                    <TableRow key={field.custom_field_id}>
                      <TableCell>{field.field_label}</TableCell>
                      <TableCell><Chip label={field.field_name} size="small" variant="outlined" /></TableCell>
                      <TableCell>
                        <Chip 
                          label={field.field_type} 
                          size="small" 
                          color={field.field_type === 'select' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {field.is_required ? (
                          <Chip label="Required" size="small" color="warning" />
                        ) : (
                          <Chip label="Optional" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleOpenCustomFieldFormDialog(field)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteCustomField(field.custom_field_id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomFieldsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Custom Field Form Dialog */}
      <Dialog
        open={customFieldFormDialog}
        onClose={handleCloseCustomFieldFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCustomField ? 'Edit Custom Field' : 'Add Custom Field'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Field Label"
                value={customFieldFormData.field_label}
                onChange={(e) => setCustomFieldFormData({ ...customFieldFormData, field_label: e.target.value })}
                required
                helperText="Display name shown to users"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Field Name"
                value={customFieldFormData.field_name}
                onChange={(e) => setCustomFieldFormData({ ...customFieldFormData, field_name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                required
                disabled={!!editingCustomField}
                helperText="Unique identifier (lowercase, no spaces)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Field Type</InputLabel>
                <Select
                  value={customFieldFormData.field_type}
                  onChange={(e) => setCustomFieldFormData({ ...customFieldFormData, field_type: e.target.value as any })}
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="textarea">Text Area</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="select">Select Dropdown</MenuItem>
                  <MenuItem value="checkbox">Checkbox</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customFieldFormData.is_required}
                    onChange={(e) => setCustomFieldFormData({ ...customFieldFormData, is_required: e.target.checked })}
                  />
                }
                label="Required Field"
              />
            </Grid>
            
            {customFieldFormData.field_type === 'select' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Options (comma-separated)"
                  value={customFieldFormData.field_options.join(', ')}
                  onChange={(e) => setCustomFieldFormData({ 
                    ...customFieldFormData, 
                    // Keep empty strings so trailing comma is visible
                    field_options: e.target.value.split(',').map(o => o.trim())
                  })}
                  required
                  helperText="Enter options separated by commas, e.g., Option 1, Option 2, Option 3"
                  multiline
                  rows={2}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Placeholder"
                value={customFieldFormData.placeholder}
                onChange={(e) => setCustomFieldFormData({ ...customFieldFormData, placeholder: e.target.value })}
                helperText="Hint text shown in the input field"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Help Text"
                value={customFieldFormData.help_text}
                onChange={(e) => setCustomFieldFormData({ ...customFieldFormData, help_text: e.target.value })}
                multiline
                rows={2}
                helperText="Additional guidance for users"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Default Value"
                value={customFieldFormData.default_value}
                onChange={(e) => setCustomFieldFormData({ ...customFieldFormData, default_value: e.target.value })}
                helperText="Optional default value for new assets"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomFieldFormDialog}>Cancel</Button>
          <Button 
            onClick={handleCustomFieldSubmit} 
            variant="contained"
            disabled={!customFieldFormData.field_label || !customFieldFormData.field_name || 
                     (customFieldFormData.field_type === 'select' && customFieldFormData.field_options.length === 0)}
          >
            {editingCustomField ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Note View Dialog */}
      <Dialog
        open={noteDialog.open}
        onClose={() => setNoteDialog({ open: false, title: '', note: '', assetName: '' })}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {noteDialog.title}
          {noteDialog.assetName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 'normal' }}>
              {noteDialog.assetName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography 
            variant="body1" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.6
            }}
          >
            {noteDialog.note}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, title: '', note: '', assetName: '' })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* MOT History Modal */}
      <Dialog
        open={motHistoryModal.open}
        onClose={() => setMotHistoryModal(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Box>
              <Typography variant="h6" component="span">
                MOT & Tax History
              </Typography>
              {motHistoryModal.asset?.asset_name && (
                <Typography variant="body2" color="text.secondary">
                  {motHistoryModal.asset.asset_name}
                  {motHistoryModal.asset.number_plate && ` · ${motHistoryModal.asset.number_plate}`}
                  {motHistoryModal.asset.vehicle_make && ` · ${motHistoryModal.asset.vehicle_make} ${motHistoryModal.asset.vehicle_model || ''}`}
                  {motHistoryModal.asset.vehicle_year && ` (${motHistoryModal.asset.vehicle_year})`}
                  {motHistoryModal.asset.vehicle_colour && ` · ${motHistoryModal.asset.vehicle_colour}`}
                </Typography>
              )}
            </Box>
          </Box>
          {motHistoryModal.asset?.number_plate && (
            <Tooltip
              title={
                !allowMotTaxTab
                  ? 'MOT / Tax is not enabled. A super admin can enable it under Features & Access.'
                  : 'Refresh MOT data from DVSA'
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={motHistoryModal.refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
                  disabled={!allowMotTaxTab || motHistoryModal.refreshing || motHistoryModal.loading}
                  onClick={() => motHistoryModal.asset && refreshMotCheck(motHistoryModal.asset.asset_id)}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {motHistoryModal.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Current MOT & Tax Status */}
              {motHistoryModal.asset && (motHistoryModal.asset.mot_status || motHistoryModal.asset.tax_status) && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  {motHistoryModal.asset.mot_status && (() => {
                    const cat = getMotStatusCategory(motHistoryModal.asset!.mot_due_date, motHistoryModal.asset!.mot_status);
                    const color = cat === 'expired' ? 'error' : cat === 'due_soon' ? 'warning' : 'success';
                    return (
                      <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180, borderColor: `${color}.main`, borderWidth: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>MOT Status</Typography>
                        <Chip
                          label={cat === 'expired' ? 'Expired' : cat === 'due_soon' ? 'Due Soon' : 'Valid'}
                          color={color as any}
                          size="small"
                          sx={{ fontWeight: 700, mb: 0.5 }}
                        />
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {motHistoryModal.asset.mot_status}
                        </Typography>
                        {motHistoryModal.asset.mot_due_date && (
                          <Typography variant="body2" color="text.secondary">
                            Due: {new Date(motHistoryModal.asset.mot_due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </Typography>
                        )}
                        {motHistoryModal.asset.mot_last_checked_at && (
                          <Typography variant="caption" color="text.secondary">
                            Checked: {new Date(motHistoryModal.asset.mot_last_checked_at).toLocaleString()}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })()}
                  {motHistoryModal.asset.tax_status && (() => {
                    const cat = getTaxStatusCategory(motHistoryModal.asset!.tax_due_date, motHistoryModal.asset!.tax_status);
                    const color = cat === 'expired' ? 'error' : cat === 'due_soon' ? 'warning' : 'success';
                    return (
                      <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180, borderColor: `${color}.main`, borderWidth: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Road Tax</Typography>
                        <Chip
                          label={cat === 'expired' ? 'Untaxed' : cat === 'due_soon' ? 'Due Soon' : 'Taxed'}
                          color={color as any}
                          size="small"
                          sx={{ fontWeight: 700, mb: 0.5 }}
                        />
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {motHistoryModal.asset.tax_status}
                        </Typography>
                        {motHistoryModal.asset.tax_due_date && (
                          <Typography variant="body2" color="text.secondary">
                            Due: {new Date(motHistoryModal.asset.tax_due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </Typography>
                        )}
                        {motHistoryModal.asset.tax_last_checked_at && (
                          <Typography variant="caption" color="text.secondary">
                            Checked: {new Date(motHistoryModal.asset.tax_last_checked_at).toLocaleString()}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })()}
                </Box>
              )}

              {/* MOT History Table */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                MOT Test History
              </Typography>
              {motHistoryModal.history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No MOT history found.
                  {motHistoryModal.asset?.number_plate && allowMotTaxTab && ' Click Refresh to fetch from DVSA.'}
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Expiry</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Mileage</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Defects</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {motHistoryModal.history.map((test, idx) => {
                        const defects = Array.isArray(test.defects) ? test.defects : [];
                        const majors = defects.filter((d: any) => d.type === 'MAJOR' || d.dangerous);
                        const advisories = defects.filter((d: any) => d.type === 'ADVISORY' && !d.dangerous);
                        return (
                          <TableRow key={test.id || idx} sx={{ verticalAlign: 'top' }}>
                            <TableCell>
                              {test.completed_date
                                ? new Date(test.completed_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={test.test_result || '-'}
                                size="small"
                                color={test.test_result === 'PASSED' ? 'success' : test.test_result === 'FAILED' ? 'error' : 'default'}
                                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell>
                              {test.expiry_date
                                ? new Date(test.expiry_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {test.odometer_value
                                ? `${parseInt(test.odometer_value).toLocaleString()} ${test.odometer_unit || ''}`
                                : '-'}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              {defects.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">None</Typography>
                              ) : (
                                <Box>
                                  {majors.length > 0 && (
                                    <Box sx={{ mb: 0.5 }}>
                                      <Typography variant="caption" color="error" sx={{ fontWeight: 700, display: 'block' }}>
                                        Major ({majors.length})
                                      </Typography>
                                      {majors.map((d: any, i: number) => (
                                        <Typography key={i} variant="caption" display="block" color="error.dark" sx={{ pl: 1 }}>
                                          • {d.text}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                  {advisories.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700, display: 'block' }}>
                                        Advisory ({advisories.length})
                                      </Typography>
                                      {advisories.map((d: any, i: number) => (
                                        <Typography key={i} variant="caption" display="block" color="text.secondary" sx={{ pl: 1 }}>
                                          • {d.text}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMotHistoryModal(prev => ({ ...prev, open: false }))}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetManagement; 